import type { ComponentChildren } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
// The subpath, not the package root: the root re-exports the zod part
// schemas too, and this bundle has no other use for zod.
import { ASSISTANT_ICONS, ICON_KIND_LABELS, type IconKind } from '@chat-widget/shared/src/avatars';
import { isLauncherVideo, MAX_LAUNCHER_UPLOAD_BYTES } from '@chat-widget/shared/src/launchers';
import {
  fieldNameFromLabel,
  LEAD_FIELD_TYPE_LABELS,
  LEAD_FIELD_TYPES,
  MAX_FIELD_LABEL_CHARS,
  MAX_FIELD_OPTIONS,
  MAX_LEAD_FIELDS,
  MAX_LEAD_INTRO_CHARS,
  MAX_LEAD_TITLE_CHARS,
  MAX_LINK_DESCRIPTION_CHARS,
  MAX_LINK_LABEL_CHARS,
  MAX_LINK_URL_CHARS,
  MAX_LINKS,
  MAX_STARTER_LABEL_CHARS,
  MAX_STARTER_MESSAGE_CHARS,
  MAX_STARTERS,
  OWNER_TOGGLEABLE_TOOLS,
  type ActionLink,
  type ConversationStarter,
  type InteractionConfig,
  type LeadCaptureConfig,
  type LeadCaptureMode,
  type OwnerToggleableTool,
} from '@chat-widget/shared/src/interactions';
// Type-only, so none of parts.ts's zod schemas reach this bundle.
import type { FormFieldDef } from '@chat-widget/shared/src/parts';

/** Every animation in widget/assets/launchers/, content-hashed and listed
 * by widget/esbuild.config.mjs at build time. Drop a .gif in that folder,
 * rebuild, and it appears here for every customer — see that file. */
declare const __CW_LAUNCHER_GALLERY__: Array<{ id: string; label: string; url: string; video: boolean; isDefault: boolean }>;
const LAUNCHER_GALLERY = __CW_LAUNCHER_GALLERY__;
const DEFAULT_LAUNCHER = LAUNCHER_GALLERY.find((m) => m.isDefault);
import {
  ApiError,
  adminApi,
  dashboardApi,
  fetchAdminOverview,
  choosingPlan,
  isPaidLevel,
  planLabel,
  reachedCap,
  type AccountInfo,
  type AdminBotRow,
  type MessageCap,
  type Bot,
  type BotDetail,
  type BotOrigin,
  type ConversationRow,
  type Demo,
  type DemoBatchPlanEntry,
  type DemoBatchResult,
  type DemoBatchSkip,
  type LoggedMessage,
  type LogSummary,
  type QaOverride,
  type Source,
  type UploadedLauncher,
  type SourcePage,
  type UnansweredGroup,
  type BotAnalytics,
} from './dashboardApi';
import { BASIC_PLAN, hrefFor, priceFor } from './pricingPlans';

// The customer dashboard. Plain and functional on purpose (the brief's
// words): the same site shell, the same typography, no charts and no
// second design system. Everything here reads and writes through
// /api/dashboard, which re-checks ownership on every single call — none
// of the guards below are load-bearing for security, they're just UI.

const STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  discovering: 'Finding pages on your site…',
  discovered: 'Action needed — choose pages below',
  indexing: 'Teaching the bot…',
  processing: 'Processing…',
  ready: 'Ready',
  failed: 'Failed',
};

const PAGE_STATUS_LABELS: Record<SourcePage['status'], string> = {
  pending: 'not added yet',
  indexed: 'added',
  failed: 'failed',
  skipped: 'skipped',
};

/** The one-line description under a source. `pageCount` means "pages
 * found" while a crawl is waiting on a choice and "pages the bot knows"
 * once it's done, so the wording has to follow the status. */
function sourceSummary(source: Source): string {
  const when = `last updated ${formatDate(source.lastRun)}`;
  if (source.type === 'file') return `Uploaded file · ${source.pageCount} section(s) · ${when}`;
  switch (source.status) {
    case 'pending':
    case 'discovering':
      return 'Website · reading your site now — this usually takes under a minute';
    case 'discovered':
      return `Website · ${source.pageCount} page(s) found · nothing added to the bot yet`;
    case 'indexing':
    case 'processing':
      return 'Website · adding the pages you chose…';
    case 'ready':
      return `Website · the bot knows ${source.pageCount} page(s) · ${when}`;
    default:
      return `Website · ${when}`;
  }
}

/** Statuses where work is still happening somewhere else (a queue
 * consumer), so the page should keep polling rather than make the
 * customer guess whether to refresh. */
const IN_FLIGHT = new Set(['pending', 'discovering', 'indexing', 'processing']);

function Spinner({ small }: { small?: boolean }) {
  return <span class={`db-spinner${small ? ' db-spinner-sm' : ''}`} aria-hidden="true" />;
}

/** A button label while its action is in flight. */
function Busy({ label }: { label: string }) {
  return (
    <span class="db-busy">
      <Spinner />
      {label}
    </span>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <p class="db-loading" role="status">
      <Spinner />
      {label}
    </p>
  );
}

function ProgressBar({ pct }: { pct: number | null }) {
  return (
    <div class="db-progress-track">
      {pct === null ? (
        <div class="db-progress-fill db-progress-fill-indeterminate" />
      ) : (
        <div class="db-progress-fill" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
      )}
    </div>
  );
}

/** Progress for a wait the server can't report on (one request, one
 * answer): the bar eases toward full over `expectedMs` and the steps light
 * up in the order the server really does them. It never reaches 100% on
 * its own — only the real result finishes it. */
function TimedProgress({ steps, expectedMs, note }: { steps: string[]; expectedMs: number; note?: string }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Date.now() - started), 100);
    return () => clearInterval(timer);
  }, []);

  const pct = Math.min(94, 100 * (1 - Math.exp((-2.2 * elapsed) / expectedMs)));
  const current = Math.min(steps.length - 1, Math.floor((elapsed / expectedMs) * steps.length));

  return (
    <div class="db-progress" role="status" aria-live="polite">
      <ProgressBar pct={pct} />
      <ol class="db-progress-steps">
        {steps.map((step, i) => (
          <li
            key={step}
            class={`db-progress-step${i < current ? ' db-progress-step-done' : i === current ? ' db-progress-step-active' : ''}`}
          >
            <span class="db-progress-step-icon">{i < current ? '✓' : i === current ? <Spinner small /> : '·'}</span>
            {step}
          </li>
        ))}
      </ol>
      {note && <p class="db-progress-note">{note}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span class={`db-badge db-badge-${status}`}>
      {IN_FLIGHT.has(status) && <Spinner small />}
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function formatDate(ms: number | null): string {
  if (!ms) return 'never';
  return new Date(ms).toLocaleString();
}

// Where every "Upgrade" prompt in the dashboard goes: the pricing page,
// so an existing customer picks a tier from the real, current line-up
// (widget/src/site/Pricing.tsx) instead of being sent straight into a
// checkout for a specific plan they never chose. Pricing.tsx itself hides
// the Free card for any account that isn't eligible for it any more (see
// its own comment) — an "Upgrade" click should never be able to land back
// on Free.
const UPGRADE_URL = '/pricing';

export function ErrorNote({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  // The server's plan-limit refusals all end in "upgrade…" (see
  // upgradeError in server/src/billing/entitlements.ts) — give them a way out.
  return (
    <p class="db-error">
      {error}
      {/upgrade/i.test(error) && (
        <>
          {' '}
          <a href={UPGRADE_URL} class="db-error-action">
            Upgrade →
          </a>
        </>
      )}
    </p>
  );
}

/** "1 November", for a UTC midnight the server counts from. */
function utcDay(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** What to tell an admin whose bots reached one of their own message
 * limits: which one, and when the bots answer again. */
export function capReachedWords(cap: MessageCap): { title: string; detail: string } {
  const which = cap.period === 'day' ? "today's" : cap.period === 'week' ? "this week's" : "this month's";
  const when = cap.period === 'day' ? 'at midnight UTC' : `on ${utcDay(cap.resetsAt)}`;
  return {
    title: `Your bots have used ${which} limit of ${fmtNum(cap.limit ?? 0)} ${cap.limit === 1 ? 'message' : 'messages'}.`,
    detail: `They've stopped answering and offer visitors the contact form instead. They answer again ${when}, or as soon as you raise the limit.`,
  };
}

// Site.tsx's router re-reads the pathname on popstate, so a pushState plus
// a synthetic popstate is a client-side transition without importing the
// router here (Site imports this file — the reverse import would be a cycle).
function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

// ---- plan, trial and usage ------------------------------------------------
// One shared copy of /api/dashboard/account, so the banner and the tabs that
// lock Growth-only features agree without each fetching their own.

let accountCache: AccountInfo | null = null;
const accountListeners = new Set<(a: AccountInfo) => void>();

// Answers can land out of order, and a GET /account sent before a POST
// that changed the account (checkout/returned, trial/start) can land after
// it. Each answer is applied only if nothing newer has been: a stale read
// putting a customer who had just paid back on plain 'none' is what makes
// PlanGate start a trial.
let accountRequestSeq = 0;
let accountAppliedSeq = 0;
function publishAccount(account: AccountInfo, seq = ++accountRequestSeq): void {
  if (seq <= accountAppliedSeq) return;
  accountAppliedSeq = seq;
  accountCache = account;
  accountListeners.forEach((listener) => listener(account));
}

export async function refreshAccount(): Promise<AccountInfo | null> {
  const seq = ++accountRequestSeq;
  try {
    const { account } = await dashboardApi.getAccount();
    publishAccount(account, seq);
    return account;
  } catch {
    return null; // the banner just doesn't show; every limit is enforced server-side anyway
  }
}

// Exported for widget/src/site/Pricing.tsx, which needs the signed-in
// visitor's plan to decide whether the Free card belongs on the page.
export function useAccount(): AccountInfo | null {
  const [account, setAccount] = useState<AccountInfo | null>(accountCache);
  useEffect(() => {
    accountListeners.add(setAccount);
    // An answer that landed between the first render and this effect
    // reached no listener of ours.
    if (accountCache) setAccount(accountCache);
    else void refreshAccount();
    return () => {
      accountListeners.delete(setAccount);
    };
  }, []);
  return account;
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const fmtNum = (n: number) => n.toLocaleString('en-US');

/** `limit` null: nothing to measure against (an admin's period with no
 * limit) — the count, and an empty bar. */
export function Meter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit === null ? 0 : limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
  return (
    <div class="db-meter">
      <div class="db-meter-label">
        <span>{label}</span>
        <span>
          {fmtNum(used)} / {limit === null ? 'no limit' : fmtNum(limit)}
        </span>
      </div>
      <div class={`db-meter-track${pct >= 80 ? ' db-meter-high' : ''}`}>
        <div class="db-meter-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function UpgradeButton({ label = 'Upgrade' }: { label?: string }) {
  return (
    <a href={UPGRADE_URL} class="site-btn site-btn-primary db-plan-cta">
      {label}
    </a>
  );
}

/** What a trial that has hit its wall (day 31, or its message allowance)
 * offers instead of a bare "Upgrade": Basic, in one click, straight to its
 * monthly checkout. That's the promise the Free card makes ("Then continue
 * on Basic from $15/mo"), so it's the one place the dashboard skips the
 * pricing page — with "See all plans" right beside it for anyone who wants
 * a different tier. Price and checkout both come from the Basic plan's own
 * data in pricingPlans.tsx. */
function ContinueOnBasic() {
  const href = hrefFor(BASIC_PLAN, 'monthly');
  if (!href) return <UpgradeButton />;
  return (
    <div class="db-plan-actions">
      <a href={href} class="site-btn site-btn-primary db-plan-cta">
        Continue on Starter — ${priceFor(BASIC_PLAN, 'monthly')}/mo
      </a>
      <a href={UPGRADE_URL} class="site-btn site-btn-ghost db-plan-cta">
        See all plans
      </a>
    </div>
  );
}

/** How long the "activating your plan…" wording holds before it becomes
 * "still being confirmed" with a support address. Measured from when the
 * customer came back from Dodo, server-side, so it doesn't restart every
 * time the page is loaded. */
const CONFIRMING_SLOW_MS = 90 * 1000;

/**
 * Once per page load: if Dodo has just redirected the customer back here,
 * tell the server so before taking the marker out of the URL. That POST is
 * what makes the wait outlive this page view — `?checkout=success` is
 * thrown away by a dashboard tab click (navigateTo rewrites the path
 * without it), by the signed-in "/" → "/app" redirect, and by opening the
 * dashboard in a new tab, and when it went, so did every trace that a
 * payment was in flight: the banner dropped straight back to the free
 * trial countdown, in front of a customer who had just paid.
 */
let checkoutReturnReported = false;
function reportCheckoutReturn(): void {
  if (checkoutReturnReported) return;
  if (new URLSearchParams(window.location.search).get('checkout') !== 'success') return;
  checkoutReturnReported = true;
  void dashboardApi
    .checkoutReturned()
    .then(({ account }) => {
      publishAccount(account);
      // Only now — while the param is still in the URL, a refresh would
      // report the return again rather than losing it.
      const params = new URLSearchParams(window.location.search);
      params.delete('checkout');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    })
    .catch(() => {
      checkoutReturnReported = false; // let the next load try again
      void refreshAccount();
    });
}

/**
 * The plan banner at the top of every dashboard page: trial countdown and
 * usage, what happens when a limit is hit, and the wait after returning
 * from checkout. The webhook, not the browser coming home from Dodo, is
 * what activates a plan — so while one is outstanding this polls until it
 * lands. What's outstanding comes from the server (account.pendingCheckout),
 * not from the URL, which is why the wait now survives a refresh.
 */
export function PlanBanner() {
  const account = useAccount();
  const pending = account?.pendingCheckout ?? null;
  // `returned` is the honest dividing line: they got as far as Dodo's
  // "payment done" redirect. A checkout they merely started and abandoned
  // shouldn't claim we've received anything, so it shows nothing special.
  const awaitingPayment = pending?.returned === true && !!account && !isPaidLevel(account.level);

  // PlanGate (the component that renders this) already re-fetches account
  // status on every dashboard navigation — nothing to do here on `path`.

  // Remembering that *this* page view watched a payment land is what turns
  // the plan banner into a thank-you rather than a flat "Basic plan".
  const sawPaymentLand = useRef(false);
  useEffect(() => {
    if (awaitingPayment) sawPaymentLand.current = true;
  }, [awaitingPayment]);

  useEffect(() => {
    if (!awaitingPayment) return;
    const timer = window.setInterval(() => void refreshAccount(), 3000);
    return () => window.clearInterval(timer);
  }, [awaitingPayment]);

  if (awaitingPayment) {
    const planName = planLabel(pending!.plan);
    if (pending!.waitingMs < CONFIRMING_SLOW_MS) {
      return (
        <div class="db-plan-banner db-plan-info" role="status">
          <div class="db-plan-text">
            <strong>Payment received — activating {planName}…</strong>
            <span>This usually takes a few seconds. You can keep working.</span>
          </div>
          <Spinner small />
        </div>
      );
    }
    return (
      <div class="db-plan-banner db-plan-warn" role="status">
        <div class="db-plan-text">
          <strong>Your {planName} payment is still being confirmed.</strong>
          <span>
            This page updates itself as soon as it clears. If it hasn't after a few minutes, email{' '}
            <a href="mailto:hello@volgachat.com">hello@volgachat.com</a> — you won't be charged twice.
          </span>
        </div>
        <Spinner small />
      </div>
    );
  }

  if (!account || account.level === 'none') return null;
  if (account.level === 'unlimited') {
    // The admin: no plan to speak of, only their own message limits.
    const cap = reachedCap(account);
    if (!cap) return null;
    const words = capReachedWords(cap);
    return (
      <div class="db-plan-banner db-plan-warn" role="status">
        <div class="db-plan-text">
          <strong>{words.title}</strong>
          <span>{words.detail}</span>
        </div>
        <a class="site-btn site-btn-primary" href="/app/usage">
          Change limits
        </a>
      </div>
    );
  }

  const { limits } = account;
  const outOfMessages = account.messagesUsed >= limits.messages;

  if (account.level === 'expired') {
    return (
      <div class="db-plan-banner db-plan-danger" role="status">
        <div class="db-plan-text">
          <strong>{account.expiredReason === 'plan_ended' ? 'Your plan has ended.' : 'Your free trial has ended.'}</strong>
          <span>Your bot has stopped answering visitors. Everything you set up is saved — upgrade to switch it back on.</span>
        </div>
        {account.expiredReason === 'trial_ended' ? <ContinueOnBasic /> : <UpgradeButton />}
      </div>
    );
  }

  if (isPaidLevel(account.level)) {
    // The account's own plan name, never a hardcoded one — Basic,
    // Professional and Enterprise are each a real, distinct plan now
    // (server/src/billing/index.ts's Plan), not display-only tiers.
    const planName = planLabel(account.level);
    const onHold = account.billingStatus === 'on_hold';
    const justUpgraded = sawPaymentLand.current;
    const tone = onHold || outOfMessages ? 'db-plan-warn' : justUpgraded ? 'db-plan-success' : 'db-plan-quiet';
    return (
      <div class={`db-plan-banner ${tone}`} role="status">
        <div class="db-plan-text">
          {onHold ? (
            <>
              <strong>Your last payment didn't go through.</strong>
              <span>We're retrying it. Update your payment method from the Dodo Payments email to keep your bot answering.</span>
            </>
          ) : outOfMessages ? (
            <>
              <strong>You've used all {fmtNum(limits.messages)} messages this month.</strong>
              <span>Your bot stops answering until your allowance resets on the 1st. Need more? Email hello@volgachat.com.</span>
            </>
          ) : justUpgraded ? (
            <>
              <strong>You're on {planName} — thank you!</strong>
              <span>Your new limits are active now.</span>
            </>
          ) : (
            // A bare plan name told a customer who had just paid nothing
            // about what they had bought. Spell out the allowance, and
            // point at the two pages that go deeper.
            <>
              <strong>
                {planName} plan — {account.planEndsAt ? `ends on ${formatDay(account.planEndsAt)}` : 'active'}
              </strong>
              <span>
                {fmtNum(limits.messages)} messages a month · {fmtNum(limits.bots)} {limits.bots === 1 ? 'chatbot' : 'chatbots'}{' '}
                · {fmtNum(limits.pages)} pages · <a href="/app/usage">Usage</a> · <a href="/app/billing">Billing</a>
              </span>
            </>
          )}
        </div>
        <div class="db-plan-meters">
          <Meter label="Messages this month" used={account.messagesUsed} limit={limits.messages} />
          <Meter label="Pages" used={account.pagesUsed} limit={limits.pages} />
        </div>
        {/* On every paid plan, in the same place. Business ('enterprise') is
            the top self-serve tier, but not the top: the pricing page it
            leads to offers Custom (Contact sales), and the yearly switch. */}
        <UpgradeButton />
      </div>
    );
  }

  // Trial.
  const msLeft = (account.trialEndsAt ?? Date.now()) - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / DAY_MS));
  const endsOn = account.trialEndsAt
    ? new Date(account.trialEndsAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : '';
  const urgent = outOfMessages || daysLeft <= 3;
  const headline = outOfMessages
    ? `You've used all ${fmtNum(limits.messages)} trial messages — your bot has stopped answering.`
    : daysLeft <= 1
      ? 'Your free trial ends today.'
      : `Free trial — ${daysLeft} days left`;

  return (
    <div class={`db-plan-banner ${urgent ? 'db-plan-warn' : 'db-plan-info'}`} role="status">
      <div class="db-plan-text">
        <strong>{headline}</strong>
        <span>
          {outOfMessages
            ? 'Upgrade to keep it answering visitors. Nothing you set up is lost.'
            : `Ends ${endsOn}. Upgrade any time — your bot and content carry over.`}
        </span>
      </div>
      <div class="db-plan-meters">
        <Meter label="Messages" used={account.messagesUsed} limit={limits.messages} />
        <Meter label="Pages" used={account.pagesUsed} limit={limits.pages} />
      </div>
      {outOfMessages ? <ContinueOnBasic /> : <UpgradeButton />}
    </div>
  );
}

/**
 * What a signed-in account with level 'none' sees for a moment instead of
 * the dashboard: its free trial starting. A new account goes straight onto
 * the trial — there used to be a plan-choice screen here, which put every
 * pricing tier between a first sign-in and the dashboard; paid plans are
 * still one Upgrade click away in the trial banner. The trial row is
 * INSERT OR IGNORE server-side (server/src/billing/entitlements.ts), so
 * this can never restart a trial that has ended — an ended trial reads as
 * 'expired', not 'none', and never reaches this. The WordPress Connect flow
 * starts the trial the same way (server/src/connect/wordpress.ts).
 */
function StartingTrial() {
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    dashboardApi.startTrial().then(
      ({ account }) => {
        if (cancelled) return;
        publishAccount(account);
      },
      (err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not start your trial.');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (!error) {
    return (
      <div class="site-wrap db-page">
        <Loading label="Starting your free trial…" />
      </div>
    );
  }
  return (
    <div class="site-wrap db-page">
      <ErrorNote error={error} />
      <button type="button" class="site-btn site-btn-primary" onClick={() => setAttempt((n) => n + 1)}>
        Try again
      </button>
    </div>
  );
}

/**
 * Wraps every /app page: gates the dashboard shell itself on the account
 * having chosen a plan (trial or paid), per the product decision that the
 * dashboard isn't usable on a bare sign-in. An account waiting on a
 * checkout it has already paid for gets PlanBanner's confirmation instead
 * of a trial — someone can go straight from 'none' to a paid tier
 * without ever starting a trial, so they may still read as 'none' for the
 * few seconds before the webhook lands, and being put on a trial right
 * after paying would be the worst possible moment for it.
 */
export function PlanGate({ path, children }: { path: string; children: ComponentChildren }) {
  const account = useAccount();
  const [loadError, setLoadError] = useState<string | null>(null);
  // Only to keep the trial start from flashing in the moment between
  // Dodo's redirect landing and /account answering. Past that first
  // render it's account.pendingCheckout that matters — unlike this, it's
  // still there on the next page load.
  const [returningFromCheckout] = useState(() => new URLSearchParams(window.location.search).get('checkout') === 'success');

  useEffect(() => {
    reportCheckoutReturn();
  }, []);

  useEffect(() => {
    setLoadError(null);
    void refreshAccount().then((a) => {
      if (!a) setLoadError('Could not load your account.');
    });
  }, [path]);

  if (!account) {
    return (
      <div class="site-wrap db-page">
        {/* The error first: a spinner promising a confirmation that can't
            arrive (the account never loaded) is no help to anyone. */}
        {loadError ? (
          <ErrorNote error={`${loadError} Refresh the page to try again.`} />
        ) : returningFromCheckout ? (
          <Loading label="Confirming your payment…" />
        ) : (
          <Loading label="Loading your account…" />
        )}
      </div>
    );
  }

  if (choosingPlan(account)) {
    // Read live, not from returningFromCheckout: reportCheckoutReturn takes
    // the marker out once the server has heard about the return. Until
    // then a customer who has just paid must not be put on a trial.
    if (new URLSearchParams(window.location.search).get('checkout') === 'success') {
      return (
        <div class="site-wrap db-page">
          <Loading label="Confirming your payment…" />
        </div>
      );
    }
    return <StartingTrial />;
  }

  // The billing and usage pages open with a fuller version of everything
  // the banner says, so showing both just repeats the plan name twice
  // above the fold. The payment-confirmation states are the exception —
  // those are time-sensitive and belong on every page.
  const ownPlanPage = path.startsWith('/app/billing') || path.startsWith('/app/usage');
  if (ownPlanPage && !account.pendingCheckout?.returned) return <>{children}</>;

  return (
    <>
      <div class="site-wrap db-plan-wrap">
        <PlanBanner />
      </div>
      {children}
    </>
  );
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the text is selectable anyway.
    }
  }

  return (
    <button type="button" class="db-btn-sm" onClick={copy}>
      {copied ? 'Copied ✓' : label}
    </button>
  );
}

/** This dashboard's own origin — where widget.js is served from. */
function widgetOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'https://your-widget-domain.example';
}

/** The one thing a customer pastes on their site. Only the bot id: colour
 * and logo used to be baked in as data-accent / data-logo, which froze them
 * at whatever they were when the snippet was copied — the widget now reads
 * them from the dashboard on every page load instead, so a change in
 * Customize reaches the site without re-pasting anything. It's also the
 * proof of ownership (server/src/bots/verification.ts), which is what
 * makes adding a website and installing the chat the same single edit. */
function embedSnippet(botId: string): string {
  return `<script src="${widgetOrigin()}/v1/widget.js"\n        data-bot-id="${botId}"\n        defer></script>`;
}

/** The one-time "prove you own this domain" step and its check button.
 * Leads with the embed snippet — the customer needs it on their site
 * anyway, so pasting it once both proves ownership and installs the chat.
 * The meta tag and file stay one click away for anyone not ready to show
 * the chat yet, or whose scripts go in through a tag manager the check
 * can't see. Opens on the Install step for a newly added site, and from
 * any unverified row in its site list. */
function DomainVerifyPanel({
  botId,
  domain,
  token,
  verification,
  sourceUrl,
  onVerified,
  onDismiss,
}: {
  botId: string;
  domain: string;
  token: string;
  verification: BotDetail['verification'];
  /** The exact page the customer submitted, if any (e.g. a Wix free-tier
   * site's page lives under a path — the bare domain root 404s, so the
   * verify check needs this to find the meta tag at all). */
  sourceUrl?: string;
  /** Called once the check passes, with how it passed — 'snippet' means
   * the chat is installed there too, which callers say out loud. */
  onVerified: (method?: string) => Promise<void>;
  onDismiss?: () => void;
}) {
  const [method, setMethod] = useState<'snippet' | 'meta' | 'file'>('snippet');
  const [checking, setChecking] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const snippet = embedSnippet(botId);
  const metaTag = `<meta name="${verification.metaName}" content="${token}">`;
  const fileUrl = `https://${domain}${verification.wellKnownPath}`;
  let pageUrl = `https://${domain}/`;
  try {
    if (sourceUrl && new URL(sourceUrl).host.toLowerCase() === domain.toLowerCase()) pageUrl = sourceUrl;
  } catch {
    // Keep the domain-root fallback.
  }

  async function check() {
    setChecking(true);
    setError(null);
    try {
      const result = await dashboardApi.verifyOrigin(botId, domain, sourceUrl);
      if (result.verified) {
        // A beat at 100% so the customer sees it finish, not just vanish.
        setDone(true);
        await new Promise((resolve) => setTimeout(resolve, 900));
        await onVerified(result.method);
        return;
      }
      setError(result.error ?? 'Verification failed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div class="db-verify-panel">
      <div class="db-verify-head">
        <div>
          <strong>{method === 'snippet' ? `Add your chat snippet to ${domain}` : `Prove you own ${domain}`}</strong>
          <p class="db-muted">
            {method === 'snippet'
              ? "It's what puts the chat on your site, and it also shows us the site is yours — so it's the only code you'll need to paste."
              : "A one-time step so nobody can point a bot at a site they don't control. You'll still paste the chat snippet afterwards — it's what puts the chat on the site."}
          </p>
        </div>
      </div>

      {method === 'snippet' ? (
        <ol class="db-verify-steps">
          <li>
            Copy this snippet:
            <div class="db-code-row">
              <pre class="db-code">{snippet}</pre>
              <CopyButton text={snippet} />
            </div>
          </li>
          <li>
            Paste it just before the closing <code>&lt;/body&gt;</code> tag of <code>{pageUrl}</code> (a site-wide footer works too)
            and publish the change.
          </li>
        </ol>
      ) : (
        <div class="db-verify-methods">
          <button
            type="button"
            class={`db-verify-method${method === 'meta' ? ' db-verify-method-active' : ''}`}
            onClick={() => setMethod('meta')}
          >
            Add a meta tag
          </button>
          <button
            type="button"
            class={`db-verify-method${method === 'file' ? ' db-verify-method-active' : ''}`}
            onClick={() => setMethod('file')}
          >
            Add a verification file
          </button>
        </div>
      )}

      {method === 'snippet' ? null : method === 'meta' ? (
        <ol class="db-verify-steps">
          <li>
            Copy this tag:
            <div class="db-code-row">
              <pre class="db-code">{metaTag}</pre>
              <CopyButton text={metaTag} />
            </div>
          </li>
          <li>
            Paste it inside the <code>&lt;head&gt;</code> of your home page (<code>{pageUrl}</code>) and publish the change.
          </li>
        </ol>
      ) : (
        <ol class="db-verify-steps">
          <li>
            Create a plain text file containing only this:
            <div class="db-code-row">
              <pre class="db-code">{token}</pre>
              <CopyButton text={token} />
            </div>
          </li>
          <li>
            Publish it so it opens at:
            <div class="db-code-row">
              <pre class="db-code">{fileUrl}</pre>
              <CopyButton text={fileUrl} />
            </div>
          </li>
        </ol>
      )}

      <ErrorNote error={error} />
      {done ? (
        <div class="db-progress" role="status">
          <ProgressBar pct={100} />
          <p class="db-progress-step db-progress-step-done">
            <span class="db-progress-step-icon">✓</span>
            {domain} is verified
          </p>
        </div>
      ) : checking ? (
        <TimedProgress
          steps={[
            `Contacting ${domain}`,
            'Reading your page',
            method === 'snippet' ? 'Looking for your chat snippet' : method === 'meta' ? 'Looking for the meta tag' : 'Looking for the verification file',
          ]}
          expectedMs={12_000}
          note="Usually a few seconds — up to 20 if your site is slow to answer."
        />
      ) : (
        <div class="db-verify-check">
          <div class="db-verify-check-actions">
            <button type="button" class="site-btn site-btn-primary db-btn-lg" onClick={check}>
              I've done this — check now
            </button>
            {onDismiss && (
              <button type="button" class="db-btn-sm" onClick={onDismiss}>
                Cancel
              </button>
            )}
          </div>
          <span class="db-muted">Published changes can take a minute to go live. If the check fails, wait a moment and try again.</span>
          {/* The chat shows on the site as soon as it's verified by the
              snippet, which not everyone wants before the bot is trained —
              hence a way out that says so, rather than a bare "other
              methods". */}
          <span class="db-muted">
            {method === 'snippet' ? (
              <>
                Not ready to show the chat on your site yet?{' '}
                <button type="button" class="db-linkbtn" onClick={() => setMethod('meta')}>
                  Verify with a meta tag or file instead
                </button>
              </>
            ) : (
              <button type="button" class="db-linkbtn" onClick={() => setMethod('snippet')}>
                ← Use the chat snippet instead — one paste does both
              </button>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Bots list
// ============================================================================

/**
 * The two places a customer goes most: their bots and their leads. Leads
 * cut across every bot, and customers come back for them often, so they
 * get a tab of their own at the top left of both pages rather than a
 * link tucked into a corner or the account menu. The count is the total
 * on file, so a new one is noticeable at a glance.
 */
export function DashboardNav({ current }: { current: 'bots' | 'leads' }) {
  const [leadCount, setLeadCount] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    dashboardApi
      .listLeads({ limit: 1 })
      .then((res) => {
        if (live) setLeadCount(res.total);
      })
      .catch(() => {
        // The tab still works without its count.
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <nav class="db-mainnav" aria-label="Dashboard">
      <a
        href="/app"
        class={`db-mainnav-tab${current === 'bots' ? ' db-mainnav-active' : ''}`}
        aria-current={current === 'bots' ? 'page' : undefined}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="10" rx="2" />
          <circle cx="12" cy="5" r="2" />
          <path d="M12 7v4M8 16h.01M16 16h.01" />
        </svg>
        Your bots
      </a>
      <a
        href="/app/leads"
        class={`db-mainnav-tab${current === 'leads' ? ' db-mainnav-active' : ''}`}
        aria-current={current === 'leads' ? 'page' : undefined}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        Leads
        {leadCount !== null && leadCount > 0 && <span class="db-mainnav-count">{fmtNum(leadCount)}</span>}
      </a>
    </nav>
  );
}

export function BotsListPage() {
  const [bots, setBots] = useState<Bot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { bots } = await dashboardApi.listBots();
      setBots(bots);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your bots.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function createBot(e: Event) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { bot } = await dashboardApi.createBot(name.trim());
      navigateTo(`/app/bots/${bot.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that bot.');
      setBusy(false);
    }
  }

  async function removeBot(bot: Bot) {
    if (!confirm(`Delete "${bot.name}"? Its sources, pages, and saved answers go with it.`)) return;
    try {
      await dashboardApi.deleteBot(bot.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that bot.');
    }
  }

  return (
    <section class="site-section db-page">
      <div class="site-wrap">
        <DashboardNav current="bots" />
        <h1>Your bots</h1>
        <p class="db-sub">Each bot is one embeddable widget, with its own content and its own embed tag.</p>
        <ErrorNote error={error} />

        <form class="db-inline-form" onSubmit={createBot}>
          <input
            class="db-input"
            placeholder="New bot name (e.g. Acme Support)"
            value={name}
            maxLength={60}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="site-btn site-btn-primary" disabled={busy || !name.trim()}>
            {busy ? <Busy label="Creating…" /> : 'Create bot'}
          </button>
        </form>

        {bots === null ? (
          // A failed load already says so above — a spinner beside it would
          // promise a list that's never coming.
          error ? null : <Loading />
        ) : bots.length === 0 ? (
          <div class="site-empty-state">
            <p>You haven't created a bot yet.</p>
            <p class="db-muted">Create one above, point it at your website, and you'll have an embed tag in a few minutes.</p>
          </div>
        ) : (
          <ul class="db-list">
            {bots.map((bot) => (
              <li class="db-list-item db-bot-card" key={bot.id}>
                <div>
                  <a class="db-list-title db-bot-card-link" href={`/app/bots/${bot.id}`}>
                    {bot.name}
                  </a>
                  <p class="db-muted">
                    <code>{bot.id}</code> · created {formatDate(bot.createdAt)}
                  </p>
                </div>
                <div class="db-row-actions">
                  <a class="site-btn site-btn-primary db-btn-sm" href={`/app/bots/${bot.id}`}>
                    Open bot →
                  </a>
                  <button type="button" class="db-btn-danger" onClick={() => removeBot(bot)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// Bot detail + tabs
// ============================================================================

type TabName = 'customize' | 'interactions' | 'install' | 'sources' | 'qa' | 'conversations' | 'test';

/**
 * The setup checklist, in nav order.
 *
 * A step with a `blocker` is mandatory: the function returns what's still
 * missing (or null when satisfied), which both gates Next and tells the
 * customer what to do about it. Those steps tick only when genuinely
 * satisfied — clicking past them isn't an option, which is the point.
 *
 * A step without one has nothing to require: there's no correct number of
 * Q&A overrides, Conversations is a read-only view, and Test is something
 * we can't observe from here. Those tick once the customer moves past them.
 */
const SETUP_STEPS: Array<{ id: TabName; label: string; blocker?: (detail: BotDetail) => string | null }> = [
  {
    id: 'customize',
    label: 'Customize',
    // The server refuses to save an empty name, so in practice this catches
    // an emptied greeting — which it does save, and which would leave
    // visitors opening a chat to nothing at all.
    blocker: (d) => (d.bot.name.trim() && d.bot.greeting.trim() ? null : 'Give your bot a name and a greeting.'),
  },
  // Nothing to require: a bot with no starters, no contact form and no
  // links is a perfectly good bot — it just answers in text.
  { id: 'interactions', label: 'Interactions' },
  {
    id: 'install',
    label: 'Install',
    // Every bot needs a site to appear on, whether or not it ever reads one.
    // Verified, not live: a customer who chose the meta tag or file because
    // they're "not ready to show the chat yet" mustn't be stuck here for
    // it. Live is Finish's to require (installBlocker). It also comes before
    // Sources on purpose — crawling only works on a verified site. Names the
    // site when one was added but never checked, which is the usual way to
    // get stuck here: "add your website" reads as if they'd done nothing.
    blocker: (d) => {
      if (d.origins.some((o) => o.verified)) return null;
      const added = d.origins.map((o) => o.domain);
      return added.length > 0
        ? `Your chat snippet hasn't been found on ${added.join(', ')} yet. Paste it on the site, publish, then click "I've done this — check now".`
        : "Your chat isn't on a website yet. Add your website and paste the chat snippet on it — every bot needs a site to appear on.";
    },
  },
  // Optional: crawling a site, uploading files, or neither. A bot can be
  // set up now and given content later.
  { id: 'sources', label: 'Sources' },
  { id: 'qa', label: 'Q&A' },
  { id: 'conversations', label: 'Conversations' },
  { id: 'test', label: 'Test' },
];

/**
 * What Finish needs beyond every step's own requirement: the chat actually
 * on a site (widgetSeenAt). The server stamps that when the site is
 * verified BY the snippet on the Install step — the usual path, so this is
 * already met by the time anyone gets here — or when the widget first asks
 * for a session from there. A site verified by meta tag or file still
 * needs the snippet pasted. Null while there's no verified site at all:
 * the Install step is already saying so.
 */
function installBlocker(d: BotDetail): string | null {
  if (d.origins.some((o) => o.verified && o.widgetSeenAt !== null)) return null;
  const verified = d.origins.filter((o) => o.verified).map((o) => o.domain);
  if (verified.length === 0) return null;
  return `The chat isn't showing on ${verified.join(', ')} yet. Paste your chat snippet there, publish, and open the site once.`;
}

interface SetupProgress {
  /** Steps the customer has clicked past. Only ever ticks off the optional
   * ones — a mandatory step's tick comes from its own requirement being
   * met, so an acknowledgement is never mistaken for real content. */
  acknowledged: TabName[];
  finished: boolean;
}

const EMPTY_PROGRESS: SetupProgress = { acknowledged: [], finished: false };
const SETUP_STORAGE_PREFIX = 'volgachat:setup:';

// Per-browser, not per-account: this is a checklist, not bot state, and
// keeping it client-side avoids a schema change for something a customer
// walks through once. The tradeoff is that the acknowledged-only ticks
// don't follow them to another device — the signal-backed ones do, since
// those re-derive from the bot itself.
function loadSetupProgress(botId: string): SetupProgress {
  try {
    const raw = localStorage.getItem(SETUP_STORAGE_PREFIX + botId);
    if (!raw) return EMPTY_PROGRESS;
    const parsed = JSON.parse(raw) as Partial<SetupProgress>;
    return {
      acknowledged: Array.isArray(parsed.acknowledged) ? parsed.acknowledged : [],
      finished: parsed.finished === true,
    };
  } catch {
    // Blocked storage or hand-edited junk. Starting empty is a fine
    // outcome — the signal-backed steps tick themselves straight back.
    return EMPTY_PROGRESS;
  }
}

function saveSetupProgress(botId: string, progress: SetupProgress) {
  try {
    localStorage.setItem(SETUP_STORAGE_PREFIX + botId, JSON.stringify(progress));
  } catch {
    // Progress just won't survive a reload; nothing else depends on it.
  }
}

/** ?tab=… and ?conversation=… on a bot's page — how the Leads page and
 * lead emails link straight to one conversation. Read once, on load. */
function linkedTab(): TabName | null {
  const tab = new URLSearchParams(window.location.search).get('tab');
  const tabs: TabName[] = ['customize', 'interactions', 'install', 'sources', 'qa', 'conversations', 'test'];
  return tabs.includes(tab as TabName) ? (tab as TabName) : null;
}

function linkedConversation(): string | null {
  return new URLSearchParams(window.location.search).get('conversation');
}

export function BotDetailPage({ botId }: { botId: string }) {
  // A ?conversation= deep link (the Leads page, a lead email) opens that
  // transcript once; re-entering the Conversations tab later shows the
  // list, not the same transcript again for as long as the page lives.
  const [linkedId] = useState(linkedConversation);
  const linkedUsed = useRef(false);
  const [detail, setDetail] = useState<BotDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabName>(() => linkedTab() ?? SETUP_STEPS[0].id);
  const [qaKey, setQaKey] = useState(0);
  const [progress, setProgress] = useState<SetupProgress>(() => loadSetupProgress(botId));
  // Transient: the congratulations screen shows once, when Finish is
  // actually clicked. Coming back to a finished bot later lands on the
  // normal view with everything ticked, not a victory lap.
  const [celebrating, setCelebrating] = useState(false);
  // Set when a blocked Next/Finish is clicked — see advance(). Cleared on
  // moving to another step, so the callout doesn't follow the customer
  // around once they've read it.
  const [nudged, setNudged] = useState(false);
  useEffect(() => {
    setNudged(false);
  }, [tab]);

  // Whether the page has the bot on screen. After that, a reload (a save's,
  // or the Interactions tab's poll during a crawl) failing once keeps the
  // page as it is rather than swapping the tab, mid-edit, for an error.
  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    try {
      setDetail(await dashboardApi.getBot(botId));
      loadedRef.current = true;
      setError(null);
    } catch (err) {
      const gone = err instanceof ApiError && err.status === 404;
      if (loadedRef.current && !gone) return;
      setError(gone ? 'That bot does not exist.' : 'Could not load this bot.');
    }
  }, [botId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <section class="site-section db-page">
        <div class="site-wrap">
          <ErrorNote error={error} />
          <p>
            <a href="/app">← Back to your bots</a>
          </p>
        </div>
      </section>
    );
  }
  if (!detail) {
    return (
      <section class="site-section db-page">
        <div class="site-wrap">
          <Loading />
        </div>
      </section>
    );
  }

  // A mandatory step is done when its requirement is actually met; an
  // optional one, when the customer has moved past it.
  const stepBlocker = (step: (typeof SETUP_STEPS)[number]) => step.blocker?.(detail) ?? null;
  const stepDone = (step: (typeof SETUP_STEPS)[number]) =>
    step.blocker ? stepBlocker(step) === null : progress.acknowledged.includes(step.id);

  const stepIndex = SETUP_STEPS.findIndex((s) => s.id === tab);
  const onLastStep = stepIndex === SETUP_STEPS.length - 1;
  const doneCount = SETUP_STEPS.filter(stepDone).length;

  // Next is gated on this step's own requirement. Finish is gated on every
  // one of them, plus the chat being live on a site — otherwise jumping
  // straight to the last step would let a customer "complete" a bot that
  // has no content or isn't on their site.
  const currentBlocker = stepBlocker(SETUP_STEPS[stepIndex]);
  const outstanding = SETUP_STEPS.filter((s) => stepBlocker(s) !== null);
  const notInstalled = installBlocker(detail);
  // On Finish, each unfinished step's own requirement, not just its name —
  // "Still to do: Install." said where to look but not what was missing.
  const blockedReason = onLastStep
    ? outstanding.length === 1
      ? `${outstanding[0].label} isn't finished. ${stepBlocker(outstanding[0])}`
      : outstanding.length > 1
        ? `${outstanding.length} steps aren't finished. ${outstanding.map((s) => `${s.label}: ${stepBlocker(s)}`).join(' ')}`
        : notInstalled
    : currentBlocker;
  // Where that gets fixed, when it's somewhere other than here — the nudge
  // offers to go straight there. A bot that's verified but not live is
  // fixed on Install too: that's where the snippet is.
  const fixStepId: TabName | null = onLastStep ? (outstanding[0]?.id ?? (notInstalled ? 'install' : null)) : null;
  const fixStep = fixStepId && fixStepId !== tab ? SETUP_STEPS.find((s) => s.id === fixStepId) : undefined;

  /** Next (or Finish on the last step): ticks the current step off and
   * moves on. Finishing is what puts up the completion screen. From the
   * bottom bar, it also scrolls back up so the new step starts at its top. */
  function advance(scrollToTop = false) {
    if (blockedReason) {
      // The button isn't disabled, on purpose: a greyed-out Finish with the
      // reason in small print under the step counter went unnoticed.
      // Clicking it makes the reason the loudest thing in the bar instead.
      setNudged(true);
      return;
    }
    const acknowledged = progress.acknowledged.includes(tab) ? progress.acknowledged : [...progress.acknowledged, tab];
    const next: SetupProgress = { acknowledged, finished: progress.finished || onLastStep };
    setProgress(next);
    saveSetupProgress(botId, next);
    if (onLastStep) {
      setCelebrating(true);
    } else {
      setTab(SETUP_STEPS[stepIndex + 1].id);
    }
    if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Same bar above and below the tab, so a customer who scrolled to the end
  // of a tall step doesn't have to scroll back up to find Next.
  const setupBar = (position: 'top' | 'bottom') => (
    <div class={`db-setup-bar db-setup-bar-${position}${nudged && blockedReason ? ' db-setup-bar-nudged' : ''}`}>
      <div class="db-setup-status">
        <span class="db-setup-progress">
          {/* "Complete" has to keep being true: deleting every
              source after finishing puts the bot back to work. */}
          {progress.finished && outstanding.length === 0 && notInstalled === null
            ? `Setup complete — ${doneCount} of ${SETUP_STEPS.length} done`
            : `Step ${stepIndex + 1} of ${SETUP_STEPS.length} · ${doneCount} done`}
        </span>
        {blockedReason && !nudged && <span class="db-setup-blocked">{blockedReason}</span>}
      </div>
      <button
        type="button"
        class={`site-btn site-btn-primary${blockedReason ? ' db-setup-btn-blocked' : ''}`}
        onClick={() => advance(position === 'bottom')}
        aria-disabled={blockedReason !== null}
        title={blockedReason ?? undefined}
      >
        {onLastStep ? 'Finish' : 'Next'}
      </button>
      {blockedReason && nudged && (
        <p class="db-setup-nudge" role="alert">
          <strong>Not yet — </strong>
          {blockedReason}
          {fixStep && (
            <>
              {' '}
              <button
                type="button"
                class="db-linkbtn"
                onClick={() => {
                  setTab(fixStep.id);
                  if (position === 'bottom') window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Go to {fixStep.label} →
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );

  return (
    <section class="site-section db-page">
      <div class="site-wrap">
        <p class="db-muted">
          <a href="/app">← Your bots</a>
        </p>
        <h1>{detail.bot.name}</h1>
        <p class="db-sub">
          Bot id <code>{detail.bot.id}</code>
        </p>

        {/* Where the customer is in the checklist, plus the way forward —
            above the side-nav/tab layout entirely (not nested beside it)
            so Next is the first thing on the page, not something below
            the fold on a tall tab. Hidden during the completion screen,
            which replaces this whole flow with its own "Go to my bot". */}
        {!celebrating && setupBar('top')}

        {!detail.coverage.fitsInBudget && (
          <p class="db-warning">
            This bot has about {detail.coverage.estimatedTokens.toLocaleString()} tokens of content but only{' '}
            {detail.coverage.budgetTokens.toLocaleString()} fit in a single answer. The most relevant parts are chosen per question —
            content outside that is not used for that answer.
            {detail.coverage.exceedsScoringLimit &&
              ` Past ${detail.coverage.scoringLimit.toLocaleString()} chunks (you have ${detail.coverage.chunkCount.toLocaleString()}), some content is never even considered. Remove sources you don't need.`}
          </p>
        )}

        <div class="db-shell">
          <nav class="db-side-nav" aria-label="Setup steps">
            {SETUP_STEPS.map((step, i) => {
              const done = stepDone(step);
              return (
                <button
                  type="button"
                  key={step.id}
                  class={`db-side-tab${tab === step.id ? ' db-side-tab-active' : ''}`}
                  aria-current={tab === step.id ? 'page' : undefined}
                  aria-label={`${step.label} — ${done ? 'done' : 'not done yet'}`}
                  onClick={() => setTab(step.id)}
                >
                  <span class={`db-side-num${done ? ' db-side-num-done' : ''}`} aria-hidden="true">
                    {done ? '✓' : i + 1}
                  </span>
                  <span>{step.label}</span>
                </button>
              );
            })}
          </nav>

          {celebrating ? (
            <div class="db-shell-body">
              <div class="db-setup-done">
                <span class="db-setup-done-mark" aria-hidden="true">
                  ✓
                </span>
                <h2>Your chatbot is ready for visitors</h2>
                <p class="db-sub">
                  It's answering from your own content on every page carrying your embed snippet. Nothing here is locked in —
                  add sources as your site grows, correct answers under Q&amp;A, and see what people actually ask under
                  Conversations.
                </p>
                <button type="button" class="site-btn site-btn-primary" onClick={() => setCelebrating(false)}>
                  Go to my bot
                </button>
              </div>
            </div>
          ) : (
            <div class="db-shell-body">
              {tab === 'install' && <InstallTab detail={detail} reload={load} />}
              {tab === 'sources' && <SourcesTab detail={detail} reload={load} onGoToInstall={() => setTab('install')} />}
              {tab === 'qa' && <QaTab botId={botId} key={qaKey} />}
              {tab === 'conversations' && (
                <ConversationsTab
                  botId={botId}
                  initialOpenId={linkedUsed.current ? null : linkedId}
                  onLinkedOpened={() => {
                    linkedUsed.current = true;
                  }}
                  onQaCreated={() => {
                    // Remounting the Q&A tab means a customer who adds an
                    // override from an unanswered question and then clicks Q&A
                    // sees it there, rather than a stale list they have to
                    // reload to trust.
                    setQaKey((k) => k + 1);
                  }}
                />
              )}
              {tab === 'customize' && <CustomizeTab detail={detail} reload={load} />}
              {tab === 'interactions' && <InteractionsTab detail={detail} reload={load} />}
              {tab === 'test' && <TestTab bot={detail.bot} />}
            </div>
          )}
        </div>

        {!celebrating && setupBar('bottom')}
      </div>
    </section>
  );
}

// ---- Sources ---------------------------------------------------------

/** Where a one-click crawl of a connected site starts: the address typed
 * on the Install step (a site served under a path needs it), else the
 * site's root. */
function crawlStartUrl(origin: BotOrigin): string {
  return origin.siteUrl ?? `https://${origin.domain}/`;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Step 4, and optional: what the bot answers from. Reading a website only
 * ever works on a site connected under Install — that's what verifying it
 * is for — so each connected site gets one Crawl button rather than an
 * address to type a second time. Uploaded files need no site at all.
 */
function SourcesTab({ detail, reload, onGoToInstall }: { detail: BotDetail; reload: () => Promise<void>; onGoToInstall: () => void }) {
  const botId = detail.bot.id;
  const [sources, setSources] = useState<Source[]>(detail.sources);
  const [pageUrl, setPageUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // What's being started right now — a site's domain, or 'page' for the
  // specific-page form — so only that button shows a spinner.
  const [crawling, setCrawling] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // Shown under the Upload button, where the person is looking — not in the
  // tab-wide error slot at the top.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [openSource, setOpenSource] = useState<string | null>(null);
  // A source waiting on a page choice opens its picker by itself — that's
  // the step people miss. `hidden` remembers the ones they've closed.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const fileRef = useRef<HTMLInputElement | null>(null);

  const verified = detail.origins.filter((o) => o.verified);
  const crawledHosts = new Set(sources.filter((s) => s.type === 'url').map((s) => hostOf(s.urlOrFilename)));

  function isPickerOpen(source: Source): boolean {
    if (source.type !== 'url') return false;
    if (openSource === source.id) return true;
    return source.status === 'discovered' && !hidden.has(source.id);
  }

  function togglePicker(source: Source) {
    if (isPickerOpen(source)) {
      setOpenSource(null);
      setHidden((prev) => new Set(prev).add(source.id));
    } else {
      setOpenSource(source.id);
      setHidden((prev) => {
        const next = new Set(prev);
        next.delete(source.id);
        return next;
      });
    }
  }

  const refresh = useCallback(async () => {
    try {
      const { sources } = await dashboardApi.listSources(botId);
      setSources(sources);
    } catch {
      // A transient failure during polling isn't worth a visible error —
      // the next tick will either succeed or the customer will act.
    }
  }, [botId]);

  // Crawling happens on a queue, so the only honest way to show progress
  // is to ask again. Polls only while something is actually in flight.
  const anyInFlight = sources.some((s) => IN_FLIGHT.has(s.status));
  useEffect(() => {
    if (!anyInFlight) return;
    const timer = setInterval(() => void refresh(), 2000);
    return () => clearInterval(timer);
  }, [anyInFlight, refresh]);

  // refresh() re-reads this tab's own source list and nothing else, but
  // the coverage warning above the tabs is driven by detail.coverage,
  // which only a full reload() refreshes.
  const wasAnyInFlight = useRef(anyInFlight);
  useEffect(() => {
    if (wasAnyInFlight.current && !anyInFlight) void reload();
    wasAnyInFlight.current = anyInFlight;
  }, [anyInFlight, reload]);

  async function crawl(target: string, key: string): Promise<void> {
    setBusy(true);
    setCrawling(key);
    setError(null);
    setNotice(null);
    try {
      await dashboardApi.addUrlSource(botId, target);
      if (key === 'page') setPageUrl('');
      setNotice(`Reading ${target} — pages appear below as they're found.`);
      await refresh();
      await reload();
    } catch (err) {
      // An address on a site that isn't connected comes back with the
      // server's own "add it under Install first" message.
      setError(err instanceof Error ? err.message : 'Could not read that address.');
    } finally {
      setBusy(false);
      setCrawling(null);
    }
  }

  async function crawlPage(e: Event) {
    e.preventDefault();
    if (!pageUrl.trim() || busy) return;
    await crawl(pageUrl.trim(), 'page');
  }

  async function uploadFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setBusy(true);
    setUploading(true);
    setError(null);
    setUploadError(null);
    setNotice(null);
    try {
      const result = await dashboardApi.uploadFile(botId, file);
      if (!result.ok && result.error) setUploadError(`Couldn't upload ${file.name}: ${result.error}`);
      await refresh();
      await reload();
    } catch (err) {
      setUploadError(`Couldn't upload ${file.name}: ${err instanceof Error ? err.message : 'something went wrong.'}`);
      // A file the server couldn't read comes back as an error (422) but
      // still leaves its source row, which counts toward the page limit —
      // refresh so the list shows it.
      await refresh().catch(() => undefined);
      await reload().catch(() => undefined);
    } finally {
      setBusy(false);
      setUploading(false);
      input.value = '';
    }
  }

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await refresh();
      // A deleted source takes its content out of the coverage warning
      // above the tabs, which only reload() re-reads.
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That action failed.');
    }
  }

  async function download(source: Source) {
    // The tab opens now, inside the click, then follows the link once it
    // arrives. Opened after the await, browsers treat it as a popup with no
    // user gesture behind it and block it — Download silently did nothing.
    const tab = window.open('', '_blank');
    try {
      const { url } = await dashboardApi.downloadUrl(botId, source.id);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      setError(err instanceof Error ? err.message : 'Could not create a download link.');
    }
  }

  return (
    <div class="db-tab-body">
      <p class="db-sub">
        Optional — give your bot something to answer from: your website's pages, your documents, or both. You can skip this and
        add content any time.
      </p>
      <ErrorNote error={error} />
      {notice && <p class="db-success">{notice}</p>}

      <h2 class="db-h2">Your website</h2>
      {verified.length === 0 ? (
        <div class="db-callout">
          <p>
            Install comes first — the bot can only read a website you've connected there, by pasting your chat snippet on it.
          </p>
          <button type="button" class="site-btn site-btn-primary db-btn-sm" onClick={onGoToInstall}>
            Go to Install
          </button>
        </div>
      ) : (
        <>
          <ul class="db-list">
            {verified.map((origin) => {
              const crawled = crawledHosts.has(origin.domain);
              return (
                <li class="db-list-item db-origin" key={origin.domain}>
                  <div class="db-origin-main">
                    <span class="db-list-title">{origin.domain}</span>
                    <p class="db-muted">
                      {crawled ? 'Added — see below.' : `Reads the pages linked from ${crawlStartUrl(origin)} so the bot can answer from them.`}
                    </p>
                  </div>
                  <div class="db-row-actions">
                    {!crawled && (
                      <button
                        type="button"
                        class="site-btn site-btn-primary db-btn-sm"
                        disabled={busy}
                        onClick={() => crawl(crawlStartUrl(origin), origin.domain)}
                      >
                        {crawling === origin.domain ? <Busy label="Starting…" /> : 'Crawl'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <details class="db-details">
            <summary>Read a specific page or section instead</summary>
            <form class="db-inline-form" onSubmit={crawlPage}>
              <input
                class="db-input"
                placeholder={`${crawlStartUrl(verified[0])}…`}
                value={pageUrl}
                onInput={(e) => setPageUrl((e.target as HTMLInputElement).value)}
              />
              <button type="submit" class="site-btn site-btn-primary" disabled={busy || !pageUrl.trim()}>
                {crawling === 'page' ? <Busy label="Starting…" /> : 'Crawl'}
              </button>
            </form>
          </details>
        </>
      )}

      <h2 class="db-h2">Documents</h2>
      <p class="db-muted">PDF, Word, or text files — no website needed.</p>
      <div>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.markdown" class="db-file-input" onChange={uploadFile} />
        <button type="button" class="site-btn site-btn-ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
          {uploading ? <Busy label="Uploading…" /> : 'Upload a file'}
        </button>
        <div role="alert">
          <ErrorNote error={uploadError} />
        </div>
      </div>

      {sources.length > 0 && (
        <>
          <h2 class="db-h2">What your bot has read</h2>
          <ul class="db-list">
            {sources.map((source) => (
              <li class="db-list-item db-source" key={source.id}>
                <div class="db-source-main">
                  <div>
                    <span class="db-list-title">{source.urlOrFilename}</span>
                    <p class="db-muted">{sourceSummary(source)}</p>
                    <StatusBadge status={source.status} />
                    {source.type === 'url' && (source.status === 'pending' || source.status === 'discovering') && (
                      <TimedProgress
                        steps={['Contacting your site', 'Following links between pages', 'Collecting what it finds']}
                        expectedMs={30_000}
                        note="Bigger sites take longer. You can leave this page — it carries on without you."
                      />
                    )}
                    <ErrorNote error={source.error} />
                  </div>
                  <div class="db-row-actions">
                    {source.type === 'url' && (
                      <>
                        <button type="button" class="db-btn-sm" onClick={() => togglePicker(source)}>
                          {isPickerOpen(source) ? 'Hide pages' : source.status === 'discovered' ? 'Choose pages' : 'Pages'}
                        </button>
                        <button type="button" class="db-btn-sm" onClick={() => act(() => dashboardApi.recrawl(botId, source.id))}>
                          Rescan site
                        </button>
                      </>
                    )}
                    {source.type === 'file' && (
                      <button type="button" class="db-btn-sm" onClick={() => download(source)}>
                        Download text
                      </button>
                    )}
                    <button type="button" class="db-btn-danger" onClick={() => act(() => dashboardApi.deleteSource(botId, source.id))}>
                      Delete
                    </button>
                  </div>
                </div>
                {isPickerOpen(source) && (
                  <PagePicker
                    botId={botId}
                    sourceId={source.id}
                    status={source.status}
                    onIndexed={async () => {
                      setOpenSource(source.id); // stay open so the per-page progress is visible
                      await refresh();
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** The checkbox list the brief asks for: everything discovery found, with
 * the junk (tag pages, archives, login screens) there to be unticked
 * before anything gets indexed. */
function PagePicker({
  botId,
  sourceId,
  status,
  onIndexed,
}: {
  botId: string;
  sourceId: string;
  status: Source['status'];
  onIndexed: () => Promise<void>;
}) {
  const [pages, setPages] = useState<SourcePage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { pages } = await dashboardApi.listPages(botId, sourceId);
      setPages(pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load pages.');
    }
  }, [botId, sourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  // While the bot is learning, pages flip from "not added yet" to "added"
  // one by one on the server — re-ask so the bar below moves for real.
  const inFlight = IN_FLIGHT.has(status);
  useEffect(() => {
    if (!inFlight) return;
    const timer = setInterval(() => void load(), 2000);
    return () => clearInterval(timer);
  }, [inFlight, load]);

  // One last read once the work stops. runIndexing marks the final page
  // 'indexed' immediately before it marks the source 'ready', so the poll
  // above is torn down by that status change before it can see the flip —
  // leaving the list showing pages as pending and the button still
  // offering to add what was just added.
  const wasInFlight = useRef(inFlight);
  useEffect(() => {
    if (wasInFlight.current && !inFlight) void load();
    wasInFlight.current = inFlight;
  }, [inFlight, load]);

  async function toggle(page: SourcePage, selected: boolean) {
    setPages((prev) => prev?.map((p) => (p.id === page.id ? { ...p, selected } : p)) ?? prev);
    try {
      await dashboardApi.setPageSelection(botId, sourceId, [{ pageId: page.id, selected }]);
    } catch {
      await load(); // put the checkbox back where the server says it is
    }
  }

  async function setAll(selected: boolean) {
    if (!pages) return;
    setBusy(true);
    setError(null);
    try {
      await dashboardApi.setPageSelection(
        botId,
        sourceId,
        pages.map((p) => ({ pageId: p.id, selected })),
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the selection.');
    } finally {
      setBusy(false);
    }
  }

  async function index() {
    setBusy(true);
    setError(null);
    try {
      await dashboardApi.indexSelected(botId, sourceId);
      await onIndexed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start indexing.');
    } finally {
      setBusy(false);
    }
  }

  // A failed first read says so — the error slot below is never reached
  // without pages, which left a spinner going for good.
  if (!pages) return error ? <ErrorNote error={error} /> : <Loading label="Loading pages…" />;
  if (pages.length === 0) return <p class="db-muted">No pages found yet — click "Rescan site" to look again.</p>;

  const selectedCount = pages.filter((p) => p.selected).length;
  const chosen = pages.filter((p) => p.selected);
  const learned = chosen.filter((p) => p.status !== 'pending').length;
  // What an index run would actually accomplish. An already-indexed page
  // would just be re-fetched and re-chunked into identical rows, so once
  // every ticked page is 'indexed' the button has nothing left to do and
  // says so rather than looking as clickable as it did the first time.
  // The server enforces this too (see indexGuard.ts) — this only keeps the
  // customer from being invited to pay for a no-op.
  const needsWork = chosen.filter((p) => p.status !== 'indexed').length;
  const allAdded = chosen.length > 0 && needsWork === 0;

  return (
    <div class="db-pages">
      <ErrorNote error={error} />
      {status === 'indexing' && (
        <div class="db-progress db-progress-panel" role="status" aria-live="polite">
          <p class="db-progress-title">
            <Spinner small /> Teaching the bot…
          </p>
          <ProgressBar pct={chosen.length ? (learned / chosen.length) * 100 : null} />
          <p class="db-progress-note">
            <strong>
              {learned} of {chosen.length}
            </strong>{' '}
            page(s) done. This finishes on its own — you can stay here or come back later.
          </p>
        </div>
      )}
      {status === 'discovered' && (
        <p class="db-callout">
          We found <strong>{pages.length} page(s)</strong> on your site. They're all ticked — untick anything the bot shouldn't learn
          from (login pages, tag archives, and the like), then click <strong>Add to bot</strong>.
        </p>
      )}
      <div class="db-pages-toolbar">
        <span class="db-muted">
          {selectedCount} of {pages.length} selected
        </span>
        <div class="db-row-actions">
          <button type="button" class="db-linkbtn" onClick={() => setAll(true)} disabled={busy}>
            Select all
          </button>
          <button type="button" class="db-linkbtn" onClick={() => setAll(false)} disabled={busy}>
            Select none
          </button>
        </div>
      </div>
      <ul class="db-page-list">
        {pages.map((page) => (
          <li key={page.id}>
            <label class="db-page-row">
              <input type="checkbox" checked={page.selected} onChange={(e) => toggle(page, (e.target as HTMLInputElement).checked)} />
              <span class="db-page-text">
                <span class="db-page-title">{page.title || page.url}</span>
                <span class="db-muted db-page-url">{page.url}</span>
                {page.status !== 'pending' && (
                  <span class={`db-page-status db-page-status-${page.status}`}>
                    {PAGE_STATUS_LABELS[page.status]}
                    {page.charCount > 0 ? ` · ${page.charCount.toLocaleString()} characters` : ''}
                  </span>
                )}
                <ErrorNote error={page.error} />
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div class="db-pages-footer">
        {allAdded && !inFlight && (
          <span class="db-muted db-pages-footer-note">
            Changed your site since? Use "Rescan site" to pick up new and updated pages.
          </span>
        )}
        <button
          type="button"
          class="site-btn site-btn-primary db-btn-lg"
          onClick={index}
          disabled={busy || inFlight || selectedCount === 0 || allAdded}
        >
          {busy ? (
            <Busy label="Starting…" />
          ) : inFlight ? (
            'Working…'
          ) : allAdded ? (
            `All ${chosen.length} page(s) added`
          ) : (
            `Add ${needsWork} page(s) to bot`
          )}
        </button>
      </div>
    </div>
  );
}

// ---- Q&A -------------------------------------------------------------

function QaTab({ botId }: { botId: string }) {
  const [overrides, setOverrides] = useState<QaOverride[] | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One request at a time: a double-click on "Add answer" saved the same
  // answer twice, and a delete that failed said nothing.
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { overrides } = await dashboardApi.listQa(botId);
      setOverrides(overrides);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load saved answers.');
    }
  }, [botId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(e: Event) {
    e.preventDefault();
    if (!question.trim() || !answer.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (editing) await dashboardApi.updateQa(botId, editing, question.trim(), answer.trim());
      else await dashboardApi.createQa(botId, question.trim(), answer.trim());
      setQuestion('');
      setAnswer('');
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that answer.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await dashboardApi.deleteQa(botId, id);
      // Deleting the answer being edited: the form would otherwise go on
      // offering "Save changes" to one that no longer exists.
      if (editing === id) {
        setEditing(null);
        setQuestion('');
        setAnswer('');
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that answer.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="db-tab-body">
      <p class="db-sub">
        An exact answer for a question you get often. These are matched before anything crawled, so they always win.
      </p>
      <ErrorNote error={error} />

      <form class="db-stack-form" onSubmit={submit}>
        <input
          class="db-input"
          placeholder="What are your opening hours?"
          value={question}
          maxLength={300}
          onInput={(e) => setQuestion((e.target as HTMLInputElement).value)}
        />
        <textarea
          class="db-input db-textarea"
          placeholder="We're open 9–5, Monday to Friday."
          value={answer}
          maxLength={2000}
          onInput={(e) => setAnswer((e.target as HTMLTextAreaElement).value)}
        />
        <div class="db-row-actions">
          <button type="submit" class="site-btn site-btn-primary" disabled={busy || !question.trim() || !answer.trim()}>
            {busy && !editing ? <Busy label="Saving…" /> : editing ? 'Save changes' : 'Add answer'}
          </button>
          {editing && (
            <button
              type="button"
              class="db-btn-sm"
              onClick={() => {
                setEditing(null);
                setQuestion('');
                setAnswer('');
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {overrides === null ? (
        // A failed load already says so above (as on the bots list).
        error ? null : <Loading />
      ) : overrides.length === 0 ? (
        <div class="site-empty-state">
          <p>No saved answers yet.</p>
        </div>
      ) : (
        <ul class="db-list">
          {overrides.map((qa) => (
            <li class="db-list-item" key={qa.id}>
              <div>
                <span class="db-list-title">{qa.question}</span>
                <p class="db-muted db-qa-answer">{qa.answer}</p>
              </div>
              <div class="db-row-actions">
                <button
                  type="button"
                  class="db-btn-sm"
                  onClick={() => {
                    setEditing(qa.id);
                    setQuestion(qa.question);
                    setAnswer(qa.answer);
                  }}
                >
                  Edit
                </button>
                <button type="button" class="db-btn-danger" disabled={busy} onClick={() => void remove(qa.id)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- Conversations (phase 10) ----------------------------------------
//
// Everything rendered here is text a stranger typed into someone's chat
// widget. It is rendered as JSX children ONLY — no dangerouslySetInnerHTML
// anywhere in this file — so Preact escapes it and a visitor who types
// `<img onerror=...>` into the chat sees those characters in their
// customer's dashboard rather than running anything in it.

const ENDED_REASON_LABELS: Record<string, string> = {
  ok: 'Answered',
  error: 'Error',
  rate_limit: 'Rate limited',
  daily_cap: 'Hit daily cap',
  plan_limit: 'Plan limit reached',
  unanswered: "Couldn't answer",
};

function relativeDay(ms: number): string {
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(ms).toLocaleDateString();
}

function ConversationsTab({
  botId,
  initialOpenId = null,
  onLinkedOpened,
  onQaCreated,
}: {
  botId: string;
  /** Open straight onto this conversation (a link from the Leads page). */
  initialOpenId?: string | null;
  /** Called once that conversation has been opened, so it isn't again. */
  onLinkedOpened?: () => void;
  onQaCreated: () => void;
}) {
  const [view, setView] = useState<'list' | 'archived' | 'unanswered' | 'analytics'>('list');
  const [summary, setSummary] = useState<LogSummary | null>(null);
  const [openId, setOpenId] = useState<string | null>(initialOpenId);
  useEffect(() => {
    if (initialOpenId) onLinkedOpened?.();
  }, []);
  // Deleting moves conversations to Archived for a few days; the count
  // labels that tab, and the notice offers Undo for the latest delete.
  const [archivedCount, setArchivedCount] = useState<number | null>(null);
  const [keepMs, setKeepMs] = useState(ARCHIVE_KEEP_FALLBACK_MS);
  const [notice, setNotice] = useState<ArchiveNotice | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [reloads, setReloads] = useState(0);

  const loadSummary = useCallback(async () => {
    try {
      const { summary } = await dashboardApi.logSummary(botId);
      setSummary(summary);
    } catch {
      setSummary(null); // a missing summary shouldn't hide the transcripts
    }
  }, [botId]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const onLoaded = useCallback((count: number, keep: number) => {
    setArchivedCount(count);
    setKeepMs(keep);
  }, []);

  async function undo() {
    if (!notice?.batch) return;
    setUndoing(true);
    try {
      const { restored } = await dashboardApi.restoreConversations(botId, { batch: notice.batch });
      setNotice({ text: `Restored ${plural(restored, 'conversation')}.`, batch: null });
      setReloads((n) => n + 1);
    } catch (err) {
      setNotice({ text: err instanceof Error ? err.message : 'Could not undo that.', batch: null, failed: true });
    } finally {
      setUndoing(false);
    }
  }

  if (openId) {
    return (
      <ConversationDetail
        botId={botId}
        conversationId={openId}
        keepMs={keepMs}
        onBack={() => setOpenId(null)}
        onChanged={(next) => setNotice(next)}
      />
    );
  }

  const tab = (key: typeof view, label: ComponentChildren) => (
    <button
      type="button"
      class={`db-subtab${view === key ? ' db-subtab-active' : ''}`}
      aria-pressed={view === key}
      onClick={() => {
        setView(key);
        if (!notice?.batch) setNotice(null);
      }}
    >
      {label}
    </button>
  );

  return (
    <div class="db-panel">
      {summary && <LogSummaryCard summary={summary} />}

      <nav class="db-subtabs">
        {tab('list', 'All conversations')}
        {tab(
          'archived',
          <>
            Archived
            {archivedCount ? <span class="db-subtab-count">{archivedCount.toLocaleString()}</span> : null}
          </>,
        )}
        <button
          type="button"
          class={`db-subtab${view === 'unanswered' ? ' db-subtab-active' : ''}`}
          onClick={() => setView('unanswered')}
        >
          Unanswered questions
        </button>
        <button
          type="button"
          class={`db-subtab${view === 'analytics' ? ' db-subtab-active' : ''}`}
          onClick={() => setView('analytics')}
        >
          Analytics
        </button>
      </nav>

      {notice && (
        <div class={`db-archive-notice${notice.failed ? ' db-archive-notice-failed' : ''}`} role="status">
          <span>{notice.text}</span>
          {notice.batch && (
            <button type="button" class="db-btn-sm" onClick={undo} disabled={undoing}>
              {undoing ? <Busy label="Undoing…" /> : 'Undo'}
            </button>
          )}
          <button type="button" class="db-archive-notice-close" aria-label="Dismiss" onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}

      {view === 'list' || view === 'archived' ? (
        <ConversationList
          key={`${view}:${reloads}`}
          botId={botId}
          archived={view === 'archived'}
          onOpen={setOpenId}
          onLoaded={onLoaded}
          onNotice={setNotice}
        />
      ) : view === 'unanswered' ? (
        <UnansweredView botId={botId} onQaCreated={onQaCreated} onOpen={setOpenId} />
      ) : (
        <AnalyticsView botId={botId} />
      )}
    </div>
  );
}

function LogSummaryCard({ summary }: { summary: LogSummary }) {
  const rate = Math.round(summary.ungroundedRate30d * 100);

  return (
    <div class="db-summary">
      <dl class="db-stats">
        <div>
          <dt>Conversations</dt>
          <dd>
            {summary.conversations7d.toLocaleString()} <span class="db-stat-unit">last 7 days</span>
          </dd>
          <dd class="db-stat-secondary">{summary.conversations30d.toLocaleString()} in 30 days</dd>
        </div>
        <div>
          <dt>Messages</dt>
          <dd>
            {summary.messages7d.toLocaleString()} <span class="db-stat-unit">last 7 days</span>
          </dd>
          <dd class="db-stat-secondary">{summary.messages30d.toLocaleString()} in 30 days</dd>
        </div>
        <div>
          <dt>Answered without your content</dt>
          <dd>
            {rate}% <span class="db-stat-unit">last 30 days</span>
          </dd>
          <dd class="db-stat-secondary">
            {summary.ungroundedCount30d.toLocaleString()} of {summary.answered30d.toLocaleString()} answers
          </dd>
        </div>
      </dl>
      <p class="db-muted db-summary-foot">
        Conversations are kept for {summary.retentionDays} days, then deleted automatically. Change that under Customize.
      </p>
    </div>
  );
}

const PAGE_SIZE = 25;
/** The server says how long Archived keeps a conversation; this is only
 * what to show before its first answer arrives. */
const ARCHIVE_KEEP_FALLBACK_MS = 3 * 86_400_000;

/** The line above the list after a delete or restore; `batch` makes it
 * offer Undo (the stamp the archive answered with). */
type ArchiveNotice = { text: string; batch: number | null; failed?: boolean };

/** "in 2 days, 5 hours" until an archived conversation is deleted for good.
 * The sweep runs hourly, so the last hour reads "within the hour". */
function untilRemoved(archivedAt: number, keepMs: number): string {
  const left = archivedAt + keepMs - Date.now();
  const hours = Math.floor(left / 3_600_000);
  if (hours < 1) return 'within the hour';
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  if (days === 0) return `in ${plural(hours, 'hour')}`;
  return `in ${plural(days, 'day')}${rest ? `, ${plural(rest, 'hour')}` : ''}`;
}

/**
 * The conversations, or (archived) the deleted ones waiting out their few
 * days before they go for good. Rows can be ticked to delete or restore
 * several at once; "all" acts on every one, not just this page. Deleting
 * never asks first (it can be undone) except for "Delete all".
 */
function ConversationList({
  botId,
  archived,
  onOpen,
  onLoaded,
  onNotice,
}: {
  botId: string;
  archived: boolean;
  onOpen: (id: string) => void;
  /** Each load reports how many are archived, and how long they're kept. */
  onLoaded: (archivedCount: number, keepMs: number) => void;
  onNotice: (notice: ArchiveNotice) => void;
}) {
  const [rows, setRows] = useState<ConversationRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [keepMs, setKeepMs] = useState(ARCHIVE_KEEP_FALLBACK_MS);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Which action is running: a row's id, 'selected' or 'all'.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Changing the filters quickly overlaps requests; only the newest one's
  // answer may land, or a slow earlier one shows the previous filter's rows.
  const requestSeq = useRef(0);
  const filtered = !archived && (unansweredOnly || !!from || !!to);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const res = await dashboardApi.listConversations(
        botId,
        archived
          ? { limit: PAGE_SIZE, offset, archived: true }
          : {
              limit: PAGE_SIZE,
              offset,
              // A date input means a whole day on the customer's own calendar:
              // from local midnight ("T00:00" without a zone parses as local
              // time — a bare date would be UTC midnight, hours off anywhere
              // else) to the start of the next day, so "to: today" includes
              // everything that happened today.
              from: from ? new Date(`${from}T00:00`).toISOString() : undefined,
              to: to ? new Date(new Date(`${to}T00:00`).getTime() + 86_400_000).toISOString() : undefined,
              unansweredOnly,
            },
      );
      if (seq !== requestSeq.current) return;
      // Emptied the last page (deleted everything on it): step back one.
      if (res.conversations.length === 0 && offset > 0) {
        setOffset(Math.max(0, offset - PAGE_SIZE));
        return;
      }
      setRows(res.conversations);
      setTotal(res.total);
      setSelected(new Set());
      setError(null);
      if (typeof res.archivedCount === 'number') {
        setKeepMs(res.archiveKeepMs);
        onLoaded(res.archivedCount, res.archiveKeepMs);
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : 'Could not load conversations.');
    }
  }, [botId, archived, offset, from, to, unansweredOnly, onLoaded]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(key: string, action: () => Promise<ArchiveNotice>) {
    setBusy(key);
    setError(null);
    try {
      onNotice(await action());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  const remove = (target: { ids: string[] } | { all: true }, key: string) =>
    run(key, async () => {
      const { archived: n, batch } = await dashboardApi.archiveConversations(botId, target);
      return { text: `Moved ${plural(n, 'conversation')} to Archived.`, batch };
    });

  const restore = (target: { ids: string[] } | { all: true }, key: string) =>
    run(key, async () => {
      const { restored } = await dashboardApi.restoreConversations(botId, target);
      return { text: `Restored ${plural(restored, 'conversation')} to All conversations.`, batch: null };
    });

  function removeAll() {
    if (!confirm(`Delete all ${plural(total, 'conversation')}? They stay in Archived for ${plural(Math.round(keepMs / 86_400_000), 'day')}, where you can restore them, then are deleted for good.`)) return;
    void remove({ all: true }, 'all');
  }

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pageIds = rows?.map((r) => r.id) ?? [];
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPage = !allOnPage && pageIds.some((id) => selected.has(id));
  const anyBusy = busy !== null;

  return (
    <div>
      {archived && (
        <p class="db-callout db-archive-intro">
          Deleted conversations wait here for {plural(Math.round(keepMs / 86_400_000), 'day')}, then are removed for good. Restore any
          of them to put it back under All conversations.
        </p>
      )}

      {!archived && (
      <div class="db-filters">
        <label class="db-field db-field-inline">
          <span>From</span>
          <input
            type="date"
            value={from}
            onInput={(e) => {
              setOffset(0);
              setFrom((e.target as HTMLInputElement).value);
            }}
          />
        </label>
        <label class="db-field db-field-inline">
          <span>To</span>
          <input
            type="date"
            value={to}
            onInput={(e) => {
              setOffset(0);
              setTo((e.target as HTMLInputElement).value);
            }}
          />
        </label>
        <label class="db-check">
          <input
            type="checkbox"
            checked={unansweredOnly}
            onChange={(e) => {
              setOffset(0);
              setUnansweredOnly((e.target as HTMLInputElement).checked);
            }}
          />
          <span>Only conversations the bot couldn't answer</span>
        </label>
      </div>
      )}

      <ErrorNote error={error} />

      {!rows && !error && <Loading />}
      {rows && rows.length === 0 && (
        <p class="db-muted">
          {archived
            ? 'Nothing in Archived. Conversations you delete come here first, so you can change your mind.'
            : `No conversations yet${filtered ? ' matching those filters' : ''}. They appear here as soon as someone chats with this bot.`}
        </p>
      )}

      {rows && rows.length > 0 && (
        <div class="db-bulkbar">
          {selected.size > 0 ? (
            <>
              <span class="db-bulkbar-count">{selected.size.toLocaleString()} selected</span>
              {archived ? (
                <button type="button" class="db-btn-sm" disabled={anyBusy} onClick={() => restore({ ids: [...selected] }, 'selected')}>
                  {busy === 'selected' ? <Busy label="Restoring…" /> : 'Restore selected'}
                </button>
              ) : (
                <button
                  type="button"
                  class="db-btn-danger db-btn-danger-solid"
                  disabled={anyBusy}
                  onClick={() => remove({ ids: [...selected] }, 'selected')}
                >
                  {busy === 'selected' ? <Busy label="Deleting…" /> : 'Delete selected'}
                </button>
              )}
              <button type="button" class="db-linkbtn" disabled={anyBusy} onClick={() => setSelected(new Set())}>
                Clear selection
              </button>
            </>
          ) : (
            <>
              <span class="db-muted">{archived ? 'Tick conversations to restore several at once.' : 'Tick conversations to delete several at once.'}</span>
              {archived ? (
                <button type="button" class="db-btn-sm" disabled={anyBusy} onClick={() => restore({ all: true }, 'all')}>
                  {busy === 'all' ? <Busy label="Restoring…" /> : `Restore all (${total.toLocaleString()})`}
                </button>
              ) : filtered ? (
                <span class="db-muted db-bulkbar-hint">Clear the filters to delete them all.</span>
              ) : (
                <button type="button" class="db-btn-danger" disabled={anyBusy} onClick={removeAll}>
                  {busy === 'all' ? <Busy label="Deleting…" /> : `Delete all (${total.toLocaleString()})`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {rows && rows.length > 0 && (
        <div class="db-table-wrap">
        <table class="db-table db-table-selectable">
          <thead>
            <tr>
              <th class="db-col-check">
                <input
                  type="checkbox"
                  aria-label="Select every conversation on this page"
                  checked={allOnPage}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPage;
                  }}
                  disabled={anyBusy}
                  onChange={() => setSelected(allOnPage ? new Set() : new Set(pageIds))}
                />
              </th>
              <th>{archived ? 'Deleted' : 'When'}</th>
              <th>First message</th>
              <th>Messages</th>
              <th>{archived ? 'Removed for good' : 'Outcome'}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const label = row.firstUserMessage ? `“${row.firstUserMessage.slice(0, 60)}”` : 'this conversation';
              const when = archived && row.archivedAt ? row.archivedAt : row.lastMessageAt;
              return (
              <tr
                key={row.id}
                class={[row.failed && !archived ? 'db-row-flagged' : '', selected.has(row.id) ? 'db-row-selected' : ''].filter(Boolean).join(' ') || undefined}
              >
                <td class="db-col-check">
                  <input
                    type="checkbox"
                    aria-label={`Select ${label}`}
                    checked={selected.has(row.id)}
                    disabled={anyBusy}
                    onChange={() => toggle(row.id)}
                  />
                </td>
                <td class="db-nowrap" title={formatDate(when)}>
                  {relativeDay(when)}
                </td>
                <td class="db-cell-wide">{row.firstUserMessage ?? <span class="db-muted">(no text)</span>}</td>
                <td>{row.messageCount}</td>
                <td>
                  {archived ? (
                    row.archivedAt && (
                      <span class="db-nowrap" title={formatDate(row.archivedAt + keepMs)}>
                        {untilRemoved(row.archivedAt, keepMs)}
                      </span>
                    )
                  ) : row.failed ? (
                    <span class="db-badge db-badge-failed">{ENDED_REASON_LABELS[row.endedReason ?? ''] ?? 'Problem'}</span>
                  ) : (
                    <span class="db-badge db-badge-ready">Answered</span>
                  )}
                </td>
                <td class="db-cell-actions">
                  <button type="button" class="db-linkbtn" onClick={() => onOpen(row.id)}>
                    Read
                  </button>
                  {archived ? (
                    <button
                      type="button"
                      class="db-linkbtn"
                      aria-label={`Restore ${label}`}
                      disabled={anyBusy}
                      onClick={() => restore({ ids: [row.id] }, row.id)}
                    >
                      {busy === row.id ? <Busy label="Restoring…" /> : 'Restore'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      class="db-linkbtn db-danger-link"
                      aria-label={`Delete ${label}`}
                      disabled={anyBusy}
                      onClick={() => remove({ ids: [row.id] }, row.id)}
                    >
                      {busy === row.id ? <Busy label="Deleting…" /> : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div class="db-pager">
          <button type="button" class="db-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            ← Newer
          </button>
          <span class="db-muted">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <button
            type="button"
            class="db-btn"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Older →
          </button>
        </div>
      )}
    </div>
  );
}

/** One stored interactive part, described rather than re-rendered live.
 * The dashboard is a transcript, not a second chat client: showing a
 * working form here would invite someone to fill it in, and there is
 * nothing on the other end of it. */
function StoredPart({ part }: { part: Record<string, unknown> }) {
  const type = String(part.type ?? 'unknown');

  if (type === 'quickReplies') {
    const options = Array.isArray(part.options) ? (part.options as Array<Record<string, unknown>>) : [];
    const chosen = typeof part.selectedOptionId === 'string' ? part.selectedOptionId : null;
    return (
      <div class="db-part">
        <span class="db-part-kind">Quick replies</span>
        <ul class="db-part-options">
          {options.map((o, i) => (
            <li key={i} class={chosen && o.id === chosen ? 'db-part-chosen' : undefined}>
              {String(o.label ?? o.id ?? '')}
              {chosen && o.id === chosen ? ' ✓ chosen' : ''}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (type === 'form') {
    const fields = Array.isArray(part.fields) ? (part.fields as Array<Record<string, unknown>>) : [];
    const values = (part.values ?? {}) as Record<string, unknown>;
    return (
      <div class="db-part">
        <span class="db-part-kind">Form{part.status === 'submitted' ? ' (submitted)' : ''}</span>
        <ul class="db-part-options">
          {fields.map((f, i) => (
            <li key={i}>
              {String(f.label ?? f.name ?? '')}
              {part.status === 'submitted' && f.name != null ? `: ${String(values[String(f.name)] ?? '')}` : ''}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div class="db-part">
        <span class="db-part-kind">Image</span> <span class="db-muted">{String(part.alt ?? part.imageId ?? '')}</span>
      </div>
    );
  }

  if (type === 'fileUpload') {
    return (
      <div class="db-part">
        <span class="db-part-kind">File</span> <span class="db-muted">{String(part.filename ?? 'attachment')}</span>
      </div>
    );
  }

  // Phase 12: the pre-chat form's answers — the lead, in full.
  if (type === 'visitorDetails') {
    const fields = Array.isArray(part.fields) ? (part.fields as Array<Record<string, unknown>>) : [];
    const values = (part.values ?? {}) as Record<string, unknown>;
    return (
      <div class="db-part">
        <span class="db-part-kind">Contact details</span>
        <ul class="db-part-options">
          {fields.map((f, i) => (
            <li key={i}>
              {String(f.label ?? f.name ?? '')}: {String(values[String(f.name)] ?? '')}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Phase 12: link buttons the assistant offered. Shown as text, not as
  // links — the transcript describes what happened, it doesn't repeat it.
  if (type === 'links') {
    const links = Array.isArray(part.links) ? (part.links as Array<Record<string, unknown>>) : [];
    return (
      <div class="db-part">
        <span class="db-part-kind">Link buttons</span>
        <ul class="db-part-options">
          {links.map((l, i) => (
            <li key={i}>
              {String(l.label ?? '')} <span class="db-muted">{String(l.url ?? '')}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div class="db-part">
      <span class="db-part-kind">{type}</span>
    </div>
  );
}

function ConversationDetail({
  botId,
  conversationId,
  keepMs,
  onBack,
  onChanged,
}: {
  botId: string;
  conversationId: string;
  keepMs: number;
  onBack: () => void;
  /** After a delete (with its Undo) or a restore: the line for the list. */
  onChanged: (notice: ArchiveNotice) => void;
}) {
  const [data, setData] = useState<{ conversation: ConversationRow; messages: LoggedMessage[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setData(await dashboardApi.getConversation(botId, conversationId));
      } catch (err) {
        setError(err instanceof ApiError && err.status === 404 ? 'That conversation no longer exists.' : 'Could not load it.');
      }
    })();
  }, [botId, conversationId]);

  // Deleting moves it to Archived and returns to the list, whose notice
  // offers Undo — so no "are you sure?" first.
  async function remove() {
    setBusy(true);
    try {
      const { batch } = await dashboardApi.deleteConversation(botId, conversationId);
      onChanged({ text: 'Moved the conversation to Archived.', batch });
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete it.');
      setBusy(false);
    }
  }

  async function restore() {
    setBusy(true);
    try {
      await dashboardApi.restoreConversations(botId, { ids: [conversationId] });
      setData((d) => d && { ...d, conversation: { ...d.conversation, archivedAt: null } });
      setRestored(true);
      onChanged({ text: 'Restored the conversation to All conversations.', batch: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore it.');
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div class="db-panel">
        <ErrorNote error={error} />
        <button type="button" class="db-linkbtn" onClick={onBack}>
          ← Back to conversations
        </button>
      </div>
    );
  }
  if (!data) return <Loading />;

  return (
    <div class="db-panel">
      <p>
        <button type="button" class="db-linkbtn" onClick={onBack}>
          ← Back to conversations
        </button>
      </p>

      {data.conversation.archivedAt ? (
        <div class="db-warning db-archived-banner">
          <span>
            In Archived since {formatDate(data.conversation.archivedAt)}. It will be deleted for good{' '}
            <strong title={formatDate(data.conversation.archivedAt + keepMs)}>{untilRemoved(data.conversation.archivedAt, keepMs)}</strong>.
          </span>
          <button type="button" class="db-btn-sm" onClick={restore} disabled={busy}>
            {busy ? <Busy label="Restoring…" /> : 'Restore'}
          </button>
        </div>
      ) : (
        restored && <p class="db-success">Restored — it's back under All conversations.</p>
      )}

      <dl class="db-meta">
        <div>
          <dt>Started</dt>
          <dd>{formatDate(data.conversation.startedAt)}</dd>
        </div>
        <div>
          <dt>Page</dt>
          <dd>{data.conversation.pageUrl ?? <span class="db-muted">not recorded</span>}</dd>
        </div>
        <div>
          <dt>Tokens</dt>
          <dd>
            {(data.conversation.inputTokens + data.conversation.outputTokens).toLocaleString()}
          </dd>
        </div>
        <div>
          <dt>Visitor</dt>
          <dd>
            {data.conversation.visitorHash ? (
              <code title="A salted hash — it groups this person's messages and identifies nobody.">
                {data.conversation.visitorHash.slice(0, 8)}
              </code>
            ) : (
              <span class="db-muted">not recorded</span>
            )}
          </dd>
        </div>
      </dl>

      <div class="db-thread">
        {data.messages.map((m) => (
          <div key={m.id} class={`db-msg db-msg-${m.role}`}>
            <div class="db-msg-head">
              <span class="db-msg-role">{m.role === 'user' ? 'Visitor' : 'Bot'}</span>
              <span class="db-muted">{formatDate(m.createdAt)}</span>
              {m.role === 'assistant' && m.grounded === false && !m.errorCode && (
                <span class="db-badge db-badge-warn" title="The bot answered without matching anything in your content.">
                  no matching content
                </span>
              )}
              {m.errorCode && <span class="db-badge db-badge-failed">{m.errorCode}</span>}
            </div>
            {m.content && <p class="db-msg-body">{m.content}</p>}
            {m.parts?.map((part, i) => <StoredPart key={i} part={part} />)}
            {m.sources && m.sources.length > 0 && (
              <p class="db-msg-sources">
                Used: {m.sources.join(' · ')}
              </p>
            )}
            {m.toolCalls && m.toolCalls.length > 0 && <p class="db-msg-sources">Tools: {m.toolCalls.join(', ')}</p>}
          </div>
        ))}
      </div>

      {!data.conversation.archivedAt && (
        <p>
          <button type="button" class="db-linkbtn db-danger-link" onClick={remove} disabled={busy}>
            {busy ? <Busy label="Deleting…" /> : 'Delete this conversation'}
          </button>
        </p>
      )}
    </div>
  );
}

// ---- Unanswered questions --------------------------------------------

/** "October 2026" for a 'YYYY-MM' month. */
function monthName(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * One chatbot, one month: conversations, leads captured, unanswered
 * questions and the most-asked questions (server/src/analytics/). The
 * three totals are kept for every month; the questions come from the
 * conversations still kept, so a month older than the retention window
 * says its list is partial rather than looking quiet.
 */
function AnalyticsView({ botId }: { botId: string }) {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [data, setData] = useState<BotAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    dashboardApi
      .analytics(botId, month)
      .then((res) => {
        if (live) setData(res.analytics);
      })
      .catch((err) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not load analytics.');
      });
    return () => {
      live = false;
    };
  }, [botId, month]);

  // A failed month switch keeps the last month on screen, picker included,
  // with the error above it — it used to replace the whole view, picker
  // and all, leaving no way to choose another month.
  if (!data) return error ? <ErrorNote error={error} /> : <Loading />;

  return (
    <div>
      <ErrorNote error={error} />
      <div class="db-analytics-head">
        <label class="db-field db-field-inline">
          <span>Month</span>
          <select value={data.month} onChange={(e) => setMonth((e.currentTarget as HTMLSelectElement).value)}>
            {data.months.map((m) => (
              <option key={m} value={m}>
                {monthName(m)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div class="db-summary">
        <dl class="db-stats">
          <div>
            <dt>Conversations</dt>
            <dd>{data.conversations.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Leads captured</dt>
            <dd>{data.leads.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Unanswered questions</dt>
            <dd>{data.unanswered.toLocaleString()}</dd>
          </div>
        </dl>
      </div>

      <h3 class="db-analytics-h3">Most-asked questions</h3>
      {data.topQuestions.length === 0 ? (
        <p class="db-muted">
          {data.questionsPartial
            ? `None to show: conversations from ${monthName(data.month)} have been deleted under this chatbot's ${data.retentionDays}-day retention setting.`
            : `No questions asked in ${monthName(data.month)} yet.`}
        </p>
      ) : (
        <>
          <ol class="db-top-questions">
            {data.topQuestions.map((q) => (
              <li key={q.question}>
                <span class="db-top-q">{q.question}</span>
                <span class="db-top-count">
                  {q.count.toLocaleString()} {q.count === 1 ? 'time' : 'times'}
                </span>
              </li>
            ))}
          </ol>
          <p class="db-muted db-analytics-note">
            Similar wordings are counted together.
            {data.questionsPartial &&
              ` Older conversations from this month have been deleted under your ${data.retentionDays}-day retention setting, so this list covers only the ones still kept. The totals above are complete.`}
          </p>
        </>
      )}
    </div>
  );
}

function UnansweredView({
  botId,
  onQaCreated,
  onOpen,
}: {
  botId: string;
  onQaCreated: () => void;
  onOpen: (id: string) => void;
}) {
  const [groups, setGroups] = useState<UnansweredGroup[] | null>(null);
  const [meta, setMeta] = useState<{ days: number; sampled: number; sampleLimit: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answering, setAnswering] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await dashboardApi.unanswered(botId);
      setGroups(res.groups);
      setMeta({ days: res.days, sampled: res.sampled, sampleLimit: res.sampleLimit });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load unanswered questions.');
    }
  }, [botId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorNote error={error} />;
  if (!groups) return <Loading />;

  if (groups.length === 0) {
    return (
      <p class="db-muted">
        Nothing unanswered in the last {meta?.days ?? 30} days. Questions land here when the bot errors, or answers without finding
        anything matching in your content.
      </p>
    );
  }

  return (
    <div>
      <p class="db-sub">
        Questions the bot couldn't answer from your content in the last {meta?.days ?? 30} days, with near-identical wordings
        grouped together. Answer one here and it becomes a Q&amp;A override immediately — no re-crawl needed.
        {meta && meta.sampled >= meta.sampleLimit && (
          <> Showing the most recent {meta.sampleLimit.toLocaleString()} only.</>
        )}
      </p>

      <ul class="db-list">
        {groups.map((group) => (
          <li key={group.question} class="db-list-item">
            <div class="db-list-main">
              <p class="db-unanswered-q">{group.question}</p>
              <p class="db-muted">
                Asked {group.count} {group.count === 1 ? 'time' : 'times'} · last {relativeDay(group.lastAskedAt)}
                {group.variants.length > 1 && <> · {group.variants.length} wordings</>}
              </p>
              {group.variants.length > 1 && (
                <details class="db-variants">
                  <summary>See how it was asked</summary>
                  <ul>
                    {group.variants.map((v, i) => (
                      <li key={i}>
                        <button type="button" class="db-linkbtn" onClick={() => onOpen(v.conversationId)}>
                          {v.question}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <button
              type="button"
              class="db-btn"
              onClick={() => setAnswering(answering === group.question ? null : group.question)}
            >
              {answering === group.question ? 'Cancel' : 'Add as Q&A override'}
            </button>

            {answering === group.question && (
              <AnswerForm
                botId={botId}
                question={group.question}
                onDone={() => {
                  setAnswering(null);
                  onQaCreated();
                  void load();
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The one-click loop the brief asks for: the question arrives prefilled,
 * the customer types only the answer, and the override is live for the
 * next question that matches it. */
function AnswerForm({ botId, question, onDone }: { botId: string; question: string; onDone: () => void }) {
  const [editableQuestion, setEditableQuestion] = useState(question);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: Event) {
    e.preventDefault();
    if (!answer.trim()) return;
    setBusy(true);
    try {
      await dashboardApi.createQa(botId, editableQuestion.trim(), answer.trim());
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that answer.');
      setBusy(false);
    }
  }

  return (
    <form class="db-answer-form" onSubmit={save}>
      <label class="db-field">
        <span>Question</span>
        <input value={editableQuestion} onInput={(e) => setEditableQuestion((e.target as HTMLInputElement).value)} maxLength={300} />
      </label>
      <label class="db-field">
        <span>Answer</span>
        <textarea
          value={answer}
          onInput={(e) => setAnswer((e.target as HTMLTextAreaElement).value)}
          rows={3}
          maxLength={2000}
          placeholder="What should the bot say?"
        />
      </label>
      <button type="submit" class="db-btn db-btn-primary" disabled={busy || !answer.trim()}>
        {busy ? <Busy label="Saving…" /> : 'Save override'}
      </button>
      <ErrorNote error={error} />
    </form>
  );
}

// ---- Customize -------------------------------------------------------

// The preset icons a customer can pick for the chat bubble/panel header —
// support agents wearing headsets, plus a few symbols — live in
// shared/src/avatars.ts, shared with the server so a new bot's default is
// the exact same bytes as the gallery tile. Earlier bots may still carry
// an emoji icon from the gallery this replaced; the "current icon" tile
// below keeps that selectable rather than silently dropping it.

/** The current choice, shown on its own above the gallery — a 48px tile
 * in a scrolling grid is easy to lose track of, especially once the
 * selected one has scrolled out of view. */
/**
 * A bot's own uploaded chat-bubble animations: the list, an upload button
 * and a delete on each.
 *
 * Worth being plain about in the UI, because customers assume otherwise:
 * the file ends up on their public website, so its URL is fetchable by
 * anyone who has it. What is actually private is the listing — these never
 * appear in another customer's dashboard.
 */
function LauncherUploads({ botId, logoUrl, onPick }: { botId: string; logoUrl: string; onPick: (url: string) => void }) {
  const [uploads, setUploads] = useState<UploadedLauncher[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      const { launchers } = await dashboardApi.listLaunchers(botId);
      setUploads(launchers);
    } catch {
      // A failed list just means no uploads shown — the built-in gallery
      // above still works, and the next render retries.
      setUploads([]);
    }
  }, [botId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // so picking the same file twice still fires
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { launcher } = await dashboardApi.uploadLauncher(botId, file);
      await load();
      onPick(launcher.url); // selecting it is almost certainly why they uploaded
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that file.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(file: string) {
    setError(null);
    try {
      await dashboardApi.deleteLauncher(botId, file);
      // The server falls back to the built-in animation when the deleted
      // file was the one in use; mirror that here so the form agrees.
      if (logoUrl === `/v1/launcher/${botId}/${file}`) onPick('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that file.');
    }
  }

  const maxMb = Math.floor(MAX_LAUNCHER_UPLOAD_BYTES / (1024 * 1024));
  return (
    <div class="db-icon-group">
      <span class="db-icon-group-label">Your uploads</span>
      <ErrorNote error={error} />
      {uploads && uploads.length > 0 && (
        <div class="db-icon-grid">
          {uploads.map((media) => (
            <div class="db-icon-upload" key={media.file}>
              <IconTile
                url={media.url}
                label={media.filename}
                selected={logoUrl === media.url}
                onSelect={() => onPick(media.url)}
              />
              <button
                type="button"
                class="db-icon-upload-remove"
                onClick={() => void remove(media.file)}
                title={`Delete ${media.filename}`}
                aria-label={`Delete ${media.filename}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div class="db-icon-upload-actions">
        <input
          ref={inputRef}
          type="file"
          accept=".gif,.webp,.png,.apng,.jpg,.jpeg,.svg,.webm,.mp4"
          class="db-file-input"
          onChange={(e) => void upload(e)}
          disabled={busy}
        />
        <button type="button" class="site-btn site-btn-ghost db-btn-sm" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? <Busy label="Uploading…" /> : 'Upload a GIF'}
        </button>
        <span class="db-muted">
          GIF, WebP, PNG, APNG or WebM, up to {maxMb} MB. Square works best. Only you see these in your dashboard, but the file
          itself is served publicly — it has to be, to show on your site.
        </span>
      </div>
    </div>
  );
}

/**
 * The enlarged preview that appears while a gallery tile is hovered or
 * focused. Fixed-position and pointer-events:none, so it floats over the
 * page without the pointer ever entering it — moving between tiles just
 * moves the preview rather than flickering it closed.
 *
 * Shown uncropped (object-fit: contain), unlike the circular tiles, which
 * crop with cover: at this size the point is to see the whole asset,
 * including the parts the bubble will cut off.
 */
function IconPreview({ url, label, anchor }: { url: string; label: string; anchor: DOMRect }) {
  const WIDTH = 248;
  const GAP = 12;
  const left = Math.min(Math.max(8, anchor.left + anchor.width / 2 - WIDTH / 2), window.innerWidth - WIDTH - 8);
  // Above the tile when there is room for it, below otherwise — the icon
  // grids run down a long page, so neither fits everywhere.
  const above = anchor.top > 320;
  const position = above
    ? { left: `${left}px`, bottom: `${window.innerHeight - anchor.top + GAP}px` }
    : { left: `${left}px`, top: `${anchor.bottom + GAP}px` };

  return (
    <div class="db-icon-preview" style={position} role="presentation">
      <LauncherThumb url={url} className="db-icon-preview-media" />
      <span class="db-icon-preview-label">{label}</span>
    </div>
  );
}

/**
 * One gallery tile: the 48px circle, plus the hover/focus preview above.
 * Focus is handled as well as hover so the preview is reachable by
 * keyboard, not only by mouse.
 */
function IconTile({
  url,
  label,
  selected,
  title,
  onSelect,
}: {
  url: string;
  label: string;
  selected: boolean;
  title?: string;
  onSelect: () => void;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const show = (e: { currentTarget: EventTarget | null }) => {
    const el = e.currentTarget as HTMLElement | null;
    if (el) setAnchor(el.getBoundingClientRect());
  };
  const hide = () => setAnchor(null);

  return (
    <>
      <button
        type="button"
        class={`db-icon-option${selected ? ' db-icon-option-selected' : ''}`}
        onClick={onSelect}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        title={title ?? label}
        aria-label={`Use the "${label}" icon`}
        aria-pressed={selected}
      >
        <LauncherThumb url={url} />
      </button>
      {anchor && <IconPreview url={url} label={label} anchor={anchor} />}
    </>
  );
}

/** A launcher tile. A .webm/.mp4 needs a muted autoplaying <video> to show
 * anything but its first frame; a .gif animates on its own in an <img>. */
function LauncherThumb({ url, className }: { url: string; className?: string }) {
  // `muted` has to be set as a PROPERTY, not just the attribute: that is
  // what Chrome and Safari check before allowing autoplay, and Preact
  // renders it as an attribute only — so without this every video sits on
  // its first frame. Same reason the loader does it by hand
  // (widget/src/loader/loader.ts's renderLauncherFace).
  const attach = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    // Autoplay can still be refused (data-saver, a stricter policy); the
    // first frame is a reasonable still, so a refusal is not an error.
    void el.play().catch(() => {});
  }, []);

  if (isLauncherVideo(url)) {
    return (
      <video
        ref={attach}
        class={className}
        src={url}
        muted
        autoPlay
        loop
        playsInline
        disablePictureInPicture
        aria-hidden="true"
      />
    );
  }
  return <img class={className} src={url} alt="" loading="lazy" />;
}

function SelectedIcon({ logoUrl }: { logoUrl: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);
  const preset = logoUrl ? ASSISTANT_ICONS.find((icon) => icon.url === logoUrl) : undefined;
  const builtIn = logoUrl ? LAUNCHER_GALLERY.find((m) => m.url === logoUrl) : undefined;
  const uploaded = logoUrl.startsWith('/v1/launcher/');

  if (!logoUrl) {
    return (
      <div class="db-icon-current" role="status">
        {DEFAULT_LAUNCHER ? (
          <LauncherThumb url={DEFAULT_LAUNCHER.url} className="db-icon-current-img" />
        ) : (
          <span class="db-icon-current-empty" aria-hidden="true">—</span>
        )}
        <div>
          <strong>Selected: talking agent (default)</strong>
          <span>The built-in animation plays on the chat bubble. The panel header shows just your bot's name.</span>
        </div>
      </div>
    );
  }
  return (
    <div class="db-icon-current" role="status">
      {failed ? (
        <span class="db-icon-current-empty" aria-hidden="true">?</span>
      ) : (
        isLauncherVideo(logoUrl) ? (
          <LauncherThumb url={logoUrl} className="db-icon-current-img" />
        ) : (
          <img class="db-icon-current-img" src={logoUrl} alt="" onError={() => setFailed(true)} />
        )
      )}
      <div>
        <strong>Selected: {preset?.label ?? builtIn?.label ?? (uploaded ? 'your uploaded animation' : 'your current icon')}</strong>
        <span>
          {failed
            ? "This image couldn't be loaded — pick another below."
            : 'Shown on the chat bubble and in the panel header. Pick another below to change it.'}
        </span>
      </div>
    </div>
  );
}

/** A static mock-up of the embedded widget's header + first-open greeting,
 * driven straight from the Customize form's local state — so a customer sees
 * name/greeting/accent/logo changes the instant they type them, with no
 * save round trip. Deliberately not the real ChatPanel: this only needs to
 * *look* right, and reusing the real component would drag in the whole
 * panel bundle (session handshake, streaming, composer logic) for a purely
 * visual preview. Try it out (the Test tab) is still the real thing. */
function BotPreview({
  name,
  greeting,
  accentColor,
  logoUrl,
  starters = [],
  preChat = null,
  contactBar = null,
}: {
  name: string;
  greeting: string;
  accentColor: string;
  logoUrl: string;
  /** Phase 12: starter button labels, as the Interactions tab edits them. */
  starters?: string[];
  /** Phase 12: the pre-chat form, when it's set to show before the chat. */
  preChat?: { title: string; fields: string[] } | null;
  /** The contact bar's field labels, when it's set to show after the
   * visitor's first message — previewed with a sample message sent. */
  contactBar?: string[] | null;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [logoUrl]);
  const accent = accentColor || '#2563EB';

  return (
    <div class="db-preview-panel">
      <header class="db-preview-header" style={{ background: accent }}>
        {logoUrl && !logoFailed && <img class="db-preview-logo" src={logoUrl} alt="" onError={() => setLogoFailed(true)} />}
        <span class="db-preview-title">{name.trim() || 'Assistant'}</span>
      </header>
      <div class="db-preview-body">
        <div class="db-preview-bubble">{greeting.trim() || 'Hi! How can I help?'}</div>
        {starters.length > 0 && (
          <div class="db-preview-starters">
            {starters.map((label, i) => (
              <span class="db-preview-starter" style={{ borderColor: accent, color: accent }} key={i}>
                {label}
              </span>
            ))}
          </div>
        )}
        {contactBar && (
          <div class="db-preview-bubble db-preview-bubble-visitor" style={{ background: accent }}>
            Hi, I have a question
          </div>
        )}
      </div>
      {contactBar && (
        <div class="db-preview-contactbar">
          <span class="db-preview-contactbar-fields">
            {contactBar.map((label, i) => (
              <span class="db-preview-contactbar-input" key={i}>
                {label}
              </span>
            ))}
          </span>
          <span class="db-preview-contactbar-save">Save</span>
        </div>
      )}
      {preChat ? (
        <div class="db-preview-form">
          <span class="db-preview-form-title">{preChat.title.trim() || 'Before we start'}</span>
          {preChat.fields.map((label, i) => (
            <span class="db-preview-form-field" key={i}>
              <span class="db-preview-form-label">{label}</span>
              <span class="db-preview-form-input" />
            </span>
          ))}
          <span class="db-preview-form-submit" style={{ background: accent }}>
            Start chat
          </span>
        </div>
      ) : (
        <div class="db-preview-composer">
          <span class="db-preview-composer-placeholder">Ask a question…</span>
          <span class="db-preview-send" style={{ background: accent }}>
            ➤
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * The autosave the Customize and Interactions tabs share — no Save button,
 * so a customer never has to remember to click one. `save` runs ~700ms
 * after the last change to `deps` (never on mount, which would re-save the
 * bot's existing values right back); never twice at once — a change made
 * while a save is in flight is saved right after it, so an older snapshot
 * can't be the one that lands last; and at once when the tab is left or
 * the page is hidden (a reload, a closed tab, a link out), with
 * `keepalive` so that request outlives the page. `onScheduled` fires when a
 * save is lined up, for the "Saving…" status.
 */
function useAutosave(save: (opts: { keepalive: boolean }) => Promise<void>, deps: unknown[], onScheduled: () => void) {
  const saveRef = useRef(save);
  saveRef.current = save;
  const scheduledRef = useRef(onScheduled);
  scheduledRef.current = onScheduled;
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const againRef = useRef(false);
  const skipFirstRef = useRef(true);

  const run = useCallback(async (keepalive = false) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    if (inFlightRef.current) {
      againRef.current = true;
      return;
    }
    inFlightRef.current = true;
    try {
      await saveRef.current({ keepalive });
    } finally {
      inFlightRef.current = false;
      if (againRef.current) {
        againRef.current = false;
        void run(false);
      }
    }
  }, []);

  useEffect(() => {
    if (skipFirstRef.current) {
      skipFirstRef.current = false;
      return;
    }
    scheduledRef.current();
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => void run(), 700);
  }, deps);

  useEffect(() => {
    const flush = (keepalive: boolean) => {
      if (timerRef.current) void run(keepalive);
    };
    const onPageHide = () => flush(true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      flush(false);
    };
  }, [run]);
}

function CustomizeTab({ detail, reload }: { detail: BotDetail; reload: () => Promise<void> }) {
  const bot = detail.bot;
  const account = useAccount();
  const [form, setForm] = useState({
    name: bot.name,
    // '' means "no title of its own": the chat shows the name.
    title: bot.title ?? '',
    greeting: bot.greeting,
    accentColor: bot.accentColor,
    logoUrl: bot.logoUrl ?? '',
    model: bot.model,
    // No dailyTokenCap here on purpose: the cap is still stored and still
    // enforced on every reply (server/src/http/routes/chat.ts), it just
    // isn't the customer's to set. Leaving it out of the form means the
    // autosave never sends it, so the stored value stands.
    recrawlEnabled: bot.recrawlEnabled,
    logRetentionDays: bot.logRetentionDays,
    // null means "we haven't set one, use the default"; the input needs a
    // string, and '' in the input then means "show no notice at all" —
    // which is a real choice a customer can make.
    loggingNotice: bot.loggingNotice ?? '',
  });
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // formRef always holds the latest form value so a debounced or flushed
  // save never sends a stale snapshot from the render that scheduled it.
  const formRef = useRef(form);
  formRef.current = form;

  async function persist({ keepalive }: { keepalive: boolean }) {
    const snapshot = formRef.current;
    // What the server won't keep is said, instead of "All changes saved":
    // it ignores an empty name (the old one stays) and clamps the
    // retention to 1–365 days, and the form used to show the rejected
    // value under a success message. Everything else still saves.
    const nameOk = snapshot.name.trim().length > 0;
    const retentionOk = Number.isInteger(snapshot.logRetentionDays) && snapshot.logRetentionDays >= 1 && snapshot.logRetentionDays <= 365;
    const problem = !nameOk
      ? 'Give the chatbot a name — an empty name is not saved; the previous one stays.'
      : !retentionOk
        ? 'Keep conversations for a whole number of days from 1 to 365 — that value is not saved.'
        : null;
    const { name, logRetentionDays, ...rest } = snapshot;
    const patch = { ...rest, ...(nameOk ? { name } : {}), ...(retentionOk ? { logRetentionDays } : {}) };
    setStatus('saving');
    try {
      setError(null);
      await (keepalive ? dashboardApi.updateBot(bot.id, patch, { keepalive: true }) : dashboardApi.updateBot(bot.id, patch));
      if (problem) {
        setStatus('error');
        setError(problem);
      } else {
        setStatus('saved');
      }
      if (!keepalive) await reload();
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not save settings.');
    }
  }

  useAutosave(persist, [form], () => setStatus('saving'));

  return (
    <div class="db-tab-body">
      <ErrorNote error={error} />
      <div class="db-settings-layout">
      <div class="db-stack-form">
        <label class="db-field">
          <span>Name</span>
          <input
            class="db-input"
            aria-label="Name"
            aria-describedby="db-name-note"
            value={form.name}
            maxLength={60}
            onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })}
          />
          <span class="db-muted db-field-note" id="db-name-note">
            Only you see this — it's how the chatbot is listed in your dashboard.
          </span>
        </label>
        <label class="db-field">
          <span>Chat title</span>
          <input
            class="db-input"
            aria-label="Chat title"
            aria-describedby="db-title-note"
            value={form.title}
            maxLength={60}
            placeholder={form.name || 'Assistant'}
            onInput={(e) => setForm({ ...form, title: (e.target as HTMLInputElement).value })}
          />
          <span class="db-muted db-field-note" id="db-title-note">
            Shown to visitors at the top of the chat, and what the assistant calls itself. Leave it empty to use the name.
          </span>
        </label>
        <label class="db-field">
          <span>Greeting</span>
          <input
            class="db-input"
            value={form.greeting}
            maxLength={300}
            onInput={(e) => setForm({ ...form, greeting: (e.target as HTMLInputElement).value })}
          />
        </label>
          <label class="db-field">
            <span>Accent colour</span>
            <input
              class="db-input db-color"
              type="color"
              value={form.accentColor}
              onInput={(e) => setForm({ ...form, accentColor: (e.target as HTMLInputElement).value })}
            />
          </label>
          {/* A div with a group role, not a <label>: a click anywhere inside a
              label is forwarded to its first button — "No icon" — so clicking
              the selected-icon preview or the help text cleared the choice. */}
          <div class="db-field" role="group" aria-labelledby="db-icon-heading">
            <span id="db-icon-heading">Icon</span>
            <SelectedIcon logoUrl={form.logoUrl} />
            <div class="db-icon-group">
              <span class="db-icon-group-label">Animations</span>
              <div class="db-icon-grid">
                {/* The default sits first and is what an empty logoUrl
                    means, so selecting it clears the field rather than
                    storing this build's hashed filename — which would go
                    stale the next time the file changes. */}
                {LAUNCHER_GALLERY.map((media) => (
                  <IconTile
                    key={media.id}
                    url={media.url}
                    label={media.label}
                    title={media.isDefault ? `${media.label} (default)` : media.label}
                    selected={media.isDefault ? !form.logoUrl : form.logoUrl === media.url}
                    onSelect={() => setForm({ ...form, logoUrl: media.isDefault ? '' : media.url })}
                  />
                ))}
              </div>
            </div>
            {/* A functional update: onPick fires when an upload finishes,
                seconds after the pick, and `form` from the render that
                started it would put back whatever was typed meanwhile. */}
            <LauncherUploads botId={detail.bot.id} logoUrl={form.logoUrl} onPick={(url) => setForm((f) => ({ ...f, logoUrl: url }))} />
            {(['agent', 'mascot', 'symbol'] as IconKind[]).map((kind) => (
              <div class="db-icon-group" key={kind}>
                <span class="db-icon-group-label">{ICON_KIND_LABELS[kind]}</span>
                <div class="db-icon-grid">
                  {ASSISTANT_ICONS.filter((icon) => icon.kind === kind).map((icon) => (
                    <IconTile
                      key={icon.id}
                      url={icon.url}
                      label={icon.label}
                      selected={form.logoUrl === icon.url}
                      onSelect={() => setForm({ ...form, logoUrl: icon.url })}
                    />
                  ))}
                </div>
              </div>
            ))}
            <span class="db-muted">
              Shown on the chat bubble and in the panel header. Animations play on the bubble; a still icon also appears
              beside your bot's name in the chat panel.
            </span>
          </div>
          {/* Where the "Powered by VolgaChat" line stands, and the way to
              remove it — the place customers look for it, next to the rest
              of how their chat looks. */}
          {account && (
            <div class="db-field">
              <span>Branding</span>
              <span class="db-muted">
                {account.showBadge ? (
                  account.limits.badge === 'addon' ? (
                    <>
                      "Powered by VolgaChat" shows under your chat. <a href="/app/billing">Remove it for $20/mo</a> in Billing.
                    </>
                  ) : (
                    <>
                      "Powered by VolgaChat" shows under your chat{account.level === 'trial' ? ' on the free trial' : ''}. It can be
                      removed on Starter for $20/mo, and Professional and Business leave it off. <a href="/pricing">See plans</a>
                    </>
                  )
                ) : (
                  'No VolgaChat branding shows in your chat.'
                )}
              </span>
            </div>
          )}
        <label class="db-field">
          <span>Model</span>
          <input class="db-input" value={form.model} disabled />
          <span class="db-muted">Not changeable yet.</span>
        </label>
        {account && (
          <label class="db-field">
            <span>Message limit</span>
            <input
              class="db-input"
              value={
                account.level === 'unlimited'
                  ? 'Unlimited'
                  : `${fmtNum(account.limits.messages)} ${account.limits.messagesPer === 'month' ? 'per month' : 'total, for the trial'}`
              }
              disabled
            />
            {/* Set by the plan (server/src/billing/entitlements.ts), not
                per-bot — a customer can't raise this by editing the bot,
                only by upgrading (see the plan banner at the top of the
                page). Links to the pricing page rather than straight to
                checkout: this is a passing mention inside a settings
                field, not a deliberate "upgrade now" click, so it should
                let the customer see what the higher tiers actually
                include first. */}
            <span class="db-muted">
              {!isPaidLevel(account.level) && account.level !== 'unlimited' ? (
                <>
                  Set by your plan — <a href="/pricing">upgrade</a> for more.
                </>
              ) : (
                'Set by your plan.'
              )}
            </span>
          </label>
        )}
        {/* The trial has no automatic re-crawl (PLAN_LIMITS' autoRefresh) —
            the cron skips it whatever this box says, so show it off and
            locked rather than ticked and quietly ignored. The stored setting
            is left alone, so it applies again the moment they upgrade. How
            often it runs is the plan's: monthly on Starter, weekly above. */}
        {account && account.limits.autoRefresh === 'off' ? (
          <label class="db-check">
            <input type="checkbox" checked={false} disabled />
            <span>
              Re-crawl this bot's URLs automatically{' '}
              <span class="db-muted">
                — on paid plans. Use "Rescan site" on the Sources tab to update now, or <a href="/pricing">upgrade</a>.
              </span>
            </span>
          </label>
        ) : (
          <label class="db-check">
            <input
              type="checkbox"
              checked={form.recrawlEnabled}
              onChange={(e) => setForm({ ...form, recrawlEnabled: (e.target as HTMLInputElement).checked })}
            />
            <span>Re-crawl this bot's URLs {account?.limits.autoRefresh === 'monthly' ? 'monthly' : 'weekly'}</span>
          </label>
        )}

        <h2 class="db-h2">Conversation logs</h2>
        <label class="db-field">
          <span>Keep conversations for</span>
          <input
            class="db-input"
            type="number"
            min={1}
            max={365}
            value={form.logRetentionDays}
            onInput={(e) => setForm({ ...form, logRetentionDays: Number((e.target as HTMLInputElement).value) })}
          />
          <span class="db-muted">
            Days. Conversations older than this are deleted automatically, every day. Visitors didn't sign up for anything — keep
            this no longer than you actually need.
          </span>
        </label>
        <label class="db-field">
          <span>Logging notice shown to visitors</span>
          <textarea
            class="db-input"
            rows={2}
            maxLength={300}
            value={form.loggingNotice}
            onInput={(e) => setForm({ ...form, loggingNotice: (e.target as HTMLTextAreaElement).value })}
          />
          <span class="db-muted">
            Shown once when someone opens the chat, before they type. Your disclosure obligations are yours, so the wording is
            yours — leave it empty to show nothing at all.
          </span>
        </label>

        <div class="db-row-actions">
          <span class="db-muted db-autosave-status" role="status" aria-live="polite">
            {status === 'saving' && 'Saving…'}
            {status === 'saved' && 'All changes saved.'}
          </span>
        </div>
      </div>

      <aside class="db-settings-preview">
        <span class="db-settings-preview-label">Live preview</span>
        <BotPreview name={form.title.trim() || form.name} greeting={form.greeting} accentColor={form.accentColor} logoUrl={form.logoUrl} />
      </aside>
      </div>
    </div>
  );
}


// ---- Interactions (phase 12) ----------------------------------------
// What the bot does beyond plain text: suggested questions under the
// greeting, a contact form (before the chat or when the assistant judges
// it useful), approved link buttons, and which of the assistant's own
// components are switched on. One autosaving form like Customize, sent
// whole: the server validates the document as a unit and refuses it with
// a reason (a bad link URL, a dropdown with no choices) rather than
// silently keeping the old version.

const LEAD_MODE_OPTIONS: Array<{ value: LeadCaptureMode; label: string; help: string }> = [
  { value: 'off', label: 'Off', help: 'Visitors chat straight away. The assistant can still ask for details in plain text.' },
  {
    value: 'before_chat',
    label: 'Before the chat starts',
    help: 'The form takes the place of the message box until it is filled in. Every conversation becomes a lead.',
  },
  {
    value: 'after_first_message',
    label: 'After the first message',
    help: 'Once a visitor has sent a message, a slim bar above the message box asks for these details. They can keep chatting without filling it in.',
  },
  {
    value: 'when_relevant',
    label: 'When the assistant decides it helps',
    help: 'Shown mid-conversation when a visitor asks for a quote, a demo, a callback, or something the bot can\'t answer.',
  },
];

function newId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** A field's stored key, derived from its label the same way the server
 * does — only so the preview and any in-progress state agree; the server
 * recomputes it on save regardless. */
function withFieldName(field: FormFieldDef, index: number): FormFieldDef {
  return { ...field, name: fieldNameFromLabel(field.label, `field_${index + 1}`) };
}

function InteractionsTab({ detail, reload }: { detail: BotDetail; reload: () => Promise<void> }) {
  const bot = detail.bot;
  const [config, setConfig] = useState<InteractionConfig>(() => ({
    starters: bot.interactions.starters.map((s) => ({ ...s })),
    leadCapture: { ...bot.interactions.leadCapture, fields: bot.interactions.leadCapture.fields.map((f) => ({ ...f })) },
    links: bot.interactions.links.map((l) => ({ ...l })),
    callout: bot.interactions.callout === true,
  }));
  const [tools, setTools] = useState<Record<OwnerToggleableTool, boolean>>({
    quickReplies: bot.tools.includes('quickReplies'),
    form: bot.tools.includes('form'),
  });
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Same autosave arrangement as CustomizeTab (useAutosave): the ref
  // always holds the newest values so a debounced or flushed save never
  // sends a stale snapshot.
  const latestRef = useRef({ config, tools });
  latestRef.current = { config, tools };

  async function persist({ keepalive }: { keepalive: boolean }) {
    const { config: current, tools: currentTools } = latestRef.current;
    // Rows the customer hasn't finished (no label yet) are left out of
    // what's saved rather than failing validation — they stay on screen
    // to be completed, and the next edit saves again.
    const payload: InteractionConfig = {
      starters: current.starters.filter((s) => s.label.trim()),
      leadCapture: {
        ...current.leadCapture,
        fields: current.leadCapture.fields.filter((f) => f.label.trim()).map(withFieldName),
      },
      links: current.links.filter((l) => l.label.trim() && l.url.trim()),
      callout: current.callout === true,
    };
    setStatus('saving');
    try {
      setError(null);
      const patch = { interactions: payload, tools: OWNER_TOGGLEABLE_TOOLS.filter((t) => currentTools[t]) };
      await (keepalive ? dashboardApi.updateBot(bot.id, patch, { keepalive: true }) : dashboardApi.updateBot(bot.id, patch));
      setStatus('saved');
      if (!keepalive) await reload();
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not save these settings.');
    }
  }

  useAutosave(persist, [config, tools], () => setStatus('saving'));

  // Links a crawl adds (server/src/crawl/autoLinks.ts) land on the server
  // while this tab may already be open with its own copy of the list.
  // They're merged in here, so an edit made afterwards doesn't save the
  // older list over them. A link removed on this screen but not yet saved
  // is the one thing a fresh read mustn't bring back.
  const removedLinkIdsRef = useRef(new Set<string>());
  useEffect(() => {
    const known = new Set(config.links.map((l) => l.id));
    const arrived = bot.interactions.links.filter((l) => !known.has(l.id) && !removedLinkIdsRef.current.has(l.id));
    if (arrived.length === 0) return;
    setConfig((c) => ({ ...c, links: [...c.links, ...arrived.map((l) => ({ ...l }))] }));
  }, [bot.interactions.links]); // on the server's list changing, with whatever is on screen then

  // A crawl in progress can add links at any moment; while one runs, the
  // bot is re-read so they show up here without leaving the tab. The
  // crawler stores the links before it marks the source finished, so the
  // read that ends this polling already has them.
  const crawlRunning = detail.sources.some((s) => IN_FLIGHT.has(s.status));
  useEffect(() => {
    if (!crawlRunning) return;
    const timer = window.setInterval(() => void reload(), 2500);
    return () => window.clearInterval(timer);
  }, [crawlRunning, reload]);

  const lead = config.leadCapture;
  const setLead = (patch: Partial<LeadCaptureConfig>) => setConfig({ ...config, leadCapture: { ...lead, ...patch } });

  function updateStarter(id: string, patch: Partial<ConversationStarter>) {
    setConfig({ ...config, starters: config.starters.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  }
  function updateLink(id: string, patch: Partial<ActionLink>) {
    setConfig({ ...config, links: config.links.map((l) => (l.id === id ? { ...l, ...patch } : l)) });
  }
  function updateField(index: number, patch: Partial<FormFieldDef>) {
    setLead({ fields: lead.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)) });
  }

  return (
    <div class="db-tab-body">
      <p class="db-sub">
        Give visitors things to click, not just a box to type in. Everything here is optional, and changes reach your site the
        next time the widget loads.
      </p>
      <ErrorNote error={error} />
      <div class="db-settings-layout">
        <div class="db-stack-form">
          <h2 class="db-h2">Buttons under the welcome message</h2>
          <p class="db-muted">
            One-tap buttons shown under the greeting until the visitor asks something of their own — the things people most
            often want, like "Book a consultation". Each one either sends a message in the chat, which the assistant answers,
            or opens a web page such as your booking page.
          </p>
          <div class="db-rows">
            {config.starters.map((starter, i) => (
              <div class="db-row-card" key={starter.id}>
                <div class="db-row-grid db-row-grid-2">
                  <label class="db-field">
                    <span>Button text</span>
                    <input
                      class="db-input"
                      value={starter.label}
                      maxLength={MAX_STARTER_LABEL_CHARS}
                      placeholder={i === 0 ? 'e.g. Book a consultation' : undefined}
                      onInput={(e) => updateStarter(starter.id, { label: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="db-field">
                    <span>When clicked</span>
                    <select
                      class="db-input"
                      value={starter.url !== undefined ? 'link' : 'message'}
                      onChange={(e) =>
                        updateStarter(starter.id, (e.target as HTMLSelectElement).value === 'link' ? { url: starter.url ?? '' } : { url: undefined })
                      }
                    >
                      <option value="message">Send a message in the chat</option>
                      <option value="link">Open a web page</option>
                    </select>
                  </label>
                </div>
                {starter.url !== undefined ? (
                  <label class="db-field">
                    <span>Web page</span>
                    <input
                      class="db-input"
                      type="url"
                      inputMode="url"
                      value={starter.url}
                      maxLength={MAX_LINK_URL_CHARS}
                      placeholder="https://… e.g. your booking page"
                      onInput={(e) => updateStarter(starter.id, { url: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                ) : (
                  <label class="db-field">
                    <span>
                      Message sent <span class="db-field-optional">(optional)</span>
                    </span>
                    <input
                      class="db-input"
                      value={starter.message}
                      maxLength={MAX_STARTER_MESSAGE_CHARS}
                      placeholder="Same as the button text"
                      onInput={(e) => updateStarter(starter.id, { message: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                )}
                <RemoveRowButton
                  label="Remove this button"
                  onClick={() => setConfig({ ...config, starters: config.starters.filter((s) => s.id !== starter.id) })}
                />
              </div>
            ))}
          </div>
          <div class="db-row-actions">
            <button
              type="button"
              class="site-btn site-btn-ghost db-btn-sm"
              disabled={config.starters.length >= MAX_STARTERS}
              onClick={() => setConfig({ ...config, starters: [...config.starters, { id: newId(), label: '', message: '' }] })}
            >
              Add a button
            </button>
            <span class="db-muted">
              {config.starters.length} of {MAX_STARTERS}
            </span>
          </div>

          <h2 class="db-h2">Contact form</h2>
          <p class="db-muted">
            Collect a visitor's details so you can follow up. Whatever they enter shows up with the conversation under
            Conversations.
          </p>
          <div class="db-radio-cards" role="radiogroup" aria-label="When to show the contact form">
            {LEAD_MODE_OPTIONS.map((option) => (
              <label class={`db-radio-card${lead.mode === option.value ? ' db-radio-card-on' : ''}`} key={option.value}>
                <input
                  type="radio"
                  name="lead-mode"
                  value={option.value}
                  checked={lead.mode === option.value}
                  onChange={() => setLead({ mode: option.value })}
                />
                <span class="db-radio-card-body">
                  <strong>{option.label}</strong>
                  <span>{option.help}</span>
                </span>
              </label>
            ))}
          </div>
          {lead.mode !== 'off' && (
            <>
              {lead.mode !== 'after_first_message' && (
                <div class="db-row-grid db-row-grid-2">
                  <label class="db-field">
                    <span>Form heading</span>
                    <input
                      class="db-input"
                      value={lead.title}
                      maxLength={MAX_LEAD_TITLE_CHARS}
                      onInput={(e) => setLead({ title: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="db-field">
                    <span>
                      Line under the heading <span class="db-field-optional">(optional)</span>
                    </span>
                    <input
                      class="db-input"
                      value={lead.intro}
                      maxLength={MAX_LEAD_INTRO_CHARS}
                      onInput={(e) => setLead({ intro: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                </div>
              )}
              <span class="db-field-group-label">Fields</span>
              {lead.mode === 'after_first_message' && (
                <p class="db-muted">Each label is shown inside its box, e.g. “Your email, so we can follow up”.</p>
              )}
              <div class="db-rows">
                {lead.fields.map((field, i) => (
                  <div class="db-row-card" key={i}>
                    <div class="db-row-grid db-row-grid-field">
                      <label class="db-field">
                        <span>Label</span>
                        <input
                          class="db-input"
                          value={field.label}
                          maxLength={MAX_FIELD_LABEL_CHARS}
                          onInput={(e) => updateField(i, { label: (e.target as HTMLInputElement).value })}
                        />
                      </label>
                      <label class="db-field">
                        <span>Type</span>
                        <select
                          class="db-input db-select"
                          value={field.fieldType}
                          onChange={(e) => updateField(i, { fieldType: (e.target as HTMLSelectElement).value as FormFieldDef['fieldType'] })}
                        >
                          {LEAD_FIELD_TYPES.map((t) => (
                            <option value={t} key={t}>
                              {LEAD_FIELD_TYPE_LABELS[t]}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label class="db-check db-check-required">
                        <input
                          type="checkbox"
                          checked={!!field.required}
                          onChange={(e) => updateField(i, { required: (e.target as HTMLInputElement).checked })}
                        />
                        <span>Required</span>
                      </label>
                    </div>
                    {field.fieldType === 'select' && (
                      <label class="db-field">
                        <span>Choices, one per line</span>
                        <textarea
                          class="db-input db-textarea db-textarea-sm"
                          rows={3}
                          value={(field.options ?? []).map((o) => o.label).join('\n')}
                          onInput={(e) =>
                            updateField(i, {
                              options: (e.target as HTMLTextAreaElement).value
                                .split('\n')
                                .map((line) => line.trim())
                                .filter(Boolean)
                                .slice(0, MAX_FIELD_OPTIONS)
                                .map((label) => ({ value: label, label })),
                            })
                          }
                        />
                      </label>
                    )}
                    <RemoveRowButton label="Remove this field" onClick={() => setLead({ fields: lead.fields.filter((_, j) => j !== i) })} />
                  </div>
                ))}
              </div>
              <div class="db-row-actions">
                <button
                  type="button"
                  class="site-btn site-btn-ghost db-btn-sm"
                  disabled={lead.fields.length >= MAX_LEAD_FIELDS}
                  onClick={() => setLead({ fields: [...lead.fields, { name: '', label: '', fieldType: 'text', required: false }] })}
                >
                  Add a field
                </button>
                <span class="db-muted">
                  {lead.fields.length} of {MAX_LEAD_FIELDS}
                </span>
              </div>
            </>
          )}

          <h2 class="db-h2">Link buttons</h2>
          <p class="db-muted">
            Pages the assistant may offer as buttons — pricing, booking, documentation. It chooses from this list only, and
            can never link anywhere else. The description is for the assistant: it says when the link is the right one.
            Crawling your website fills this in with its main pages; edit or remove any you don't want.
          </p>
          <div class="db-rows">
            {config.links.map((link) => (
              <div class="db-row-card" key={link.id}>
                <div class="db-row-grid db-row-grid-2">
                  <label class="db-field">
                    <span>Button text</span>
                    <input
                      class="db-input"
                      value={link.label}
                      maxLength={MAX_LINK_LABEL_CHARS}
                      placeholder="e.g. See pricing"
                      onInput={(e) => updateLink(link.id, { label: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="db-field">
                    <span>Link (https://…)</span>
                    <input
                      class="db-input"
                      type="url"
                      value={link.url}
                      maxLength={MAX_LINK_URL_CHARS}
                      placeholder="https://example.com/pricing"
                      onInput={(e) => updateLink(link.id, { url: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                </div>
                <label class="db-field">
                  <span>
                    When to offer it <span class="db-field-optional">(optional)</span>
                  </span>
                  <input
                    class="db-input"
                    value={link.description}
                    maxLength={MAX_LINK_DESCRIPTION_CHARS}
                    placeholder="e.g. When someone asks about plans or prices"
                    onInput={(e) => updateLink(link.id, { description: (e.target as HTMLInputElement).value })}
                  />
                </label>
                <RemoveRowButton
                  label="Remove this link"
                  onClick={() => {
                    removedLinkIdsRef.current.add(link.id);
                    setConfig({ ...config, links: config.links.filter((l) => l.id !== link.id) });
                  }}
                />
              </div>
            ))}
          </div>
          <div class="db-row-actions">
            <button
              type="button"
              class="site-btn site-btn-ghost db-btn-sm"
              disabled={config.links.length >= MAX_LINKS}
              onClick={() => setConfig({ ...config, links: [...config.links, { id: newId(), label: '', url: '', description: '' }] })}
            >
              Add a link
            </button>
            <span class="db-muted">
              {config.links.length} of {MAX_LINKS}
            </span>
          </div>

          <h2 class="db-h2">Chat invitation</h2>
          <p class="db-muted">
            An animated card beside the chat bubble that greets visitors and invites them to ask a question — so the chat gets
            noticed instead of sitting quietly in the corner.
          </p>
          <label class="db-check">
            <input
              type="checkbox"
              checked={config.callout === true}
              onChange={(e) => setConfig({ ...config, callout: (e.target as HTMLInputElement).checked })}
            />
            <span>
              <strong>Show the chat invitation</strong> — "Hi there! 👋 Got a question?" with a button that opens the chat. It
              appears a moment after the page loads and stays until the visitor opens the chat or closes it. It greets them again on every page load.
            </span>
          </label>

          <h2 class="db-h2">Assistant components</h2>
          <p class="db-muted">
            Building blocks the assistant may use on its own. Quick reply buttons go under nearly every answer, so visitors
            can tap their next question instead of typing it; the rest are used only when they fit the moment.
          </p>
          <label class="db-check">
            <input type="checkbox" checked={tools.quickReplies} onChange={(e) => setTools({ ...tools, quickReplies: (e.target as HTMLInputElement).checked })} />
            <span>
              <strong>Quick reply buttons</strong> — suggested follow-up questions and actions under the assistant's answers,
              so visitors tap instead of type
            </span>
          </label>
          <label class="db-check">
            <input type="checkbox" checked={tools.form} onChange={(e) => setTools({ ...tools, form: (e.target as HTMLInputElement).checked })} />
            <span>
              <strong>Forms</strong> — several details at once (text, email, phone, dropdowns, checkboxes) when it needs them
              together
            </span>
          </label>
          <span class="db-muted">
            The contact form and link buttons above are always available to the assistant once you've set them up.
          </span>

          <div class="db-row-actions">
            <span class="db-muted db-autosave-status" role="status" aria-live="polite">
              {status === 'saving' && 'Saving…'}
              {status === 'saved' && 'All changes saved.'}
            </span>
          </div>
        </div>

        <aside class="db-settings-preview">
          <span class="db-settings-preview-label">Live preview</span>
          <BotPreview
            name={bot.title || bot.name}
            greeting={bot.greeting}
            accentColor={bot.accentColor}
            logoUrl={bot.logoUrl ?? ''}
            starters={config.starters.map((s) => s.label.trim()).filter(Boolean)}
            preChat={lead.mode === 'before_chat' ? { title: lead.title, fields: lead.fields.map((f) => f.label.trim()).filter(Boolean) } : null}
            contactBar={lead.mode === 'after_first_message' ? lead.fields.map((f) => f.label.trim()).filter(Boolean) : null}
          />
        </aside>
      </div>
    </div>
  );
}

function RemoveRowButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" class="db-row-remove" onClick={onClick} title={label} aria-label={label}>
      ×
    </button>
  );
}

/**
 * One row per domain, kept to a line each: name, state, and what (if
 * anything) is left to do. The verification instructions only unfold for
 * the one domain the customer clicks "Verify" on — a full set of steps
 * inlined under every unverified row made the list look like a form to
 * fill in rather than a list to glance at.
 */
function OriginList({
  detail,
  reload,
  pendingDomain,
}: {
  detail: BotDetail;
  reload: () => Promise<void>;
  /** A domain whose instructions are already open above, in the "Add
   * website" panel — its row points there rather than offering a second
   * copy of the same steps. */
  pendingDomain?: string;
}) {
  const botId = detail.bot.id;
  const [verifying, setVerifying] = useState<string | null>(null);
  // Removing a website: one at a time, and a failure is said rather than
  // left as an unhandled rejection with the row still there.
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (detail.origins.length === 0) return null;
  return (
    <ul class="db-list">
      {error && (
        <li class="db-list-item" role="alert">
          <ErrorNote error={error} />
        </li>
      )}
      {detail.origins.map((origin) => {
        const live = origin.verified && origin.widgetSeenAt !== null;
        const verifyingAbove = !origin.verified && origin.domain === pendingDomain;
        return (
          <li class="db-list-item db-origin" key={origin.domain}>
            <div class="db-origin-main">
              <span class="db-list-title">{origin.domain}</span>{' '}
              {live ? (
                <span class="db-badge db-badge-ready">Live</span>
              ) : origin.verified ? (
                <span class="db-badge db-badge-ready">Verified</span>
              ) : (
                <span class="db-badge db-badge-pending">Not verified</span>
              )}
              <p class="db-muted">
                {live
                  ? `Widget last seen here ${formatDate(origin.widgetSeenAt as number)}.`
                  : origin.verified
                    ? 'Ready — paste the snippet on this site and this turns Live by itself.'
                    : verifyingAbove
                      ? 'Follow the steps above to verify it.'
                      : 'The widget is refused here until you paste the snippet on this site and click Verify.'}
              </p>
              {!origin.verified && !verifyingAbove && verifying === origin.domain && (
                <DomainVerifyPanel
                  botId={botId}
                  domain={origin.domain}
                  token={origin.verificationToken}
                  verification={detail.verification}
                  sourceUrl={origin.siteUrl ?? undefined}
                  onVerified={async () => {
                    setVerifying(null);
                    await reload();
                  }}
                  onDismiss={() => setVerifying(null)}
                />
              )}
            </div>
            <div class="db-row-actions">
              {!origin.verified && !verifyingAbove && verifying !== origin.domain && (
                <button type="button" class="site-btn site-btn-primary db-btn-sm" onClick={() => setVerifying(origin.domain)}>
                  Verify
                </button>
              )}
              <button
                type="button"
                class="db-btn-danger"
                disabled={removing !== null}
                onClick={async () => {
                  if (live && !confirm(`Remove ${origin.domain}? The widget will stop working on that site.`)) return;
                  setRemoving(origin.domain);
                  setError(null);
                  try {
                    await dashboardApi.deleteOrigin(botId, origin.domain);
                    await reload();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Could not remove that website.');
                  } finally {
                    setRemoving(null);
                  }
                }}
              >
                Remove
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---- Install ---------------------------------------------------------

/** How often the waiting callout re-asks whether the widget has loaded yet. */
const WIDGET_SEEN_POLL_MS = 5000;

/**
 * Shown while a site is verified but the widget hasn't loaded there yet —
 * the one state where Finish is disabled and nothing on screen is moving,
 * so a customer can't tell whether to wait, refresh, or do something.
 * widgetSeenAt is stamped by the server when the widget first loads on a
 * verified site, so this polls for it. It reads the bot directly rather
 * than through reload(): reload() swaps the whole page for an error on a
 * single failed request, which a background poll mustn't do. reload()
 * only runs once the widget has actually been seen, to unlock Finish.
 */
function WaitingForWidget({ botId, domains, reload }: { botId: string; domains: string[]; reload: () => Promise<void> }) {
  useEffect(() => {
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const fresh = await dashboardApi.getBot(botId);
        if (!stopped && fresh.origins.some((o) => o.verified && o.widgetSeenAt !== null)) {
          stopped = true;
          clearInterval(timer);
          await reload();
        }
      } catch {
        // A transient failure isn't worth a visible error — the next tick
        // will try again.
      }
    }, WIDGET_SEEN_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [botId, reload]);

  const list = domains.join(', ');
  return (
    <div class="db-callout db-embed-status db-embed-waiting" role="status" aria-live="polite">
      <p class="db-embed-waiting-title">
        <Spinner small />
        Waiting for the widget to load on <strong>{list}</strong>…
      </p>
      <ProgressBar pct={null} />
      <p class="db-embed-waiting-note">
        Paste the snippet on {domains.length > 1 ? 'one of these sites' : 'your site'}, publish it, then open the site once in a
        browser. This page checks every few seconds and unlocks <strong>Finish</strong> as soon as the widget shows up — no need
        to refresh.
      </p>
    </div>
  );
}

/**
 * Step 3, and mandatory for every bot, crawled or not: put the chat on the
 * customer's website. One snippet, pasted once — finding it on the site is
 * also what proves the site is theirs (server/src/bots/verification.ts),
 * which Sources needs before it will read any page there. Everything about
 * where the chat runs lives here: the sites, another site, the snippet
 * again, and the CSP note.
 */
function InstallTab({ detail, reload }: { detail: BotDetail; reload: () => Promise<void> }) {
  const botId = detail.bot.id;
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [addingAnother, setAddingAnother] = useState(false);
  // The site whose instructions are open in the main panel. Starts on an
  // unverified site while nothing is verified yet, so a customer who left
  // to edit their site comes back to the same instructions — the server
  // already holds the domain, its token and the address they typed, so
  // there's nothing else to restore.
  const [active, setActive] = useState<string | null>(() =>
    detail.origins.some((o) => o.verified) ? null : (detail.origins.find((o) => !o.verified)?.domain ?? null),
  );
  const activeOrigin = active ? detail.origins.find((o) => o.domain === active && !o.verified) : undefined;

  const verified = detail.origins.filter((o) => o.verified);
  const live = verified.filter((o) => o.widgetSeenAt !== null);
  // Verified by meta tag or file, so the snippet still has to go on — the
  // one state where it's shown open rather than folded away.
  const waiting = live.length === 0 && verified.length > 0;
  const showForm = detail.origins.length === 0 || addingAnother;
  const origin = widgetOrigin();
  const snippet = embedSnippet(botId);

  async function addSite(e: Event) {
    e.preventDefault();
    if (!url.trim() || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { origin: added } = await dashboardApi.addOrigin(botId, url.trim());
      setUrl('');
      setAddingAnother(false);
      await reload();
      if (!added.verified) setActive(added.domain);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that website.');
    } finally {
      setBusy(false);
    }
  }

  async function onVerified(domain: string, method?: string) {
    setActive(null);
    await reload();
    setNotice(
      method === 'snippet'
        ? `Found your snippet on ${domain} — you're installed. Next, Sources can read your site's pages so the bot answers from them (optional).`
        : `${domain} is verified. The chat appears there once the snippet below is on the site.`,
    );
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the snippet is selectable below anyway.
    }
  }

  const snippetBlock = (
    <div class="site-code-block">
      <div class="site-code-header">
        <span>index.html</span>
        <button type="button" class="site-copy-btn" onClick={copy}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      <pre>
        <code>{snippet}</code>
      </pre>
    </div>
  );

  return (
    <div class="db-tab-body">
      <p class="db-sub">
        Put the chat on your website. It's one snippet, pasted once: it adds the chat and shows us the site is yours — which
        Sources needs before it can read your pages.
      </p>
      <ErrorNote error={error} />
      {notice && <p class="db-success">{notice}</p>}

      {showForm && (
        <form class="db-inline-form" onSubmit={addSite}>
          <input
            class="db-input"
            placeholder="https://your-site.com"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="site-btn site-btn-primary" disabled={busy || !url.trim()}>
            {busy ? <Busy label="Adding…" /> : 'Add website'}
          </button>
          {addingAnother && detail.origins.length > 0 && (
            <button type="button" class="db-btn-sm" onClick={() => setAddingAnother(false)}>
              Cancel
            </button>
          )}
        </form>
      )}

      {activeOrigin && (
        <DomainVerifyPanel
          botId={botId}
          domain={activeOrigin.domain}
          token={activeOrigin.verificationToken}
          verification={detail.verification}
          sourceUrl={activeOrigin.siteUrl ?? undefined}
          onVerified={(method) => onVerified(activeOrigin.domain, method)}
          onDismiss={() => {
            // Closing the instructions before verifying means they've
            // changed their mind about this address: put it back in the box
            // to fix, and drop the row rather than leave an "Unverified"
            // site behind. Adding it again brings the same steps back.
            setUrl(activeOrigin.siteUrl ?? activeOrigin.domain);
            setActive(null);
            setAddingAnother(true);
            void dashboardApi.deleteOrigin(botId, activeOrigin.domain).then(reload, () => undefined);
          }}
        />
      )}

      {live.length > 0 ? (
        <p class="db-success db-embed-status">
          ✓ The chat is live on {live.map((o) => o.domain).join(', ')}. Nothing more to paste.
        </p>
      ) : (
        waiting && (
          <>
            <WaitingForWidget botId={botId} domains={verified.map((o) => o.domain)} reload={reload} />
            <p class="db-sub">Paste this just before the closing &lt;/body&gt; tag on your site.</p>
            {snippetBlock}
          </>
        )
      )}

      {/* Only once there's more to it than the one site the panel above is
          already about — a list repeating "follow the steps above" under
          the steps themselves is noise. */}
      {detail.origins.some((o) => o.domain !== activeOrigin?.domain) && (
        <>
          <h2 class="db-h2">Your sites</h2>
          <OriginList detail={detail} reload={reload} pendingDomain={activeOrigin?.domain} />
          {!showForm && (
            <p class="db-muted">
              Want the chat on another site too?{' '}
              <button type="button" class="db-linkbtn" onClick={() => setAddingAnother(true)}>
                Add another site
              </button>
            </p>
          )}
        </>
      )}

      {/* Nothing to paste it into until a website is added. */}
      {!waiting && detail.origins.length > 0 && (
        <details class="db-details">
          <summary>Show the snippet</summary>
          <p class="db-sub">The same snippet works on every site listed here. Paste it just before the closing &lt;/body&gt; tag.</p>
          {snippetBlock}
        </details>
      )}

      <details class="db-details">
        <summary>Does your site set a Content-Security-Policy? (most don't)</summary>
        <p class="db-sub">If it does, it needs to allow this domain in three directives:</p>
        <pre class="db-code">{`script-src ${origin};\nframe-src ${origin};\nconnect-src ${origin};`}</pre>
        <p class="db-muted">
          A strict <code>style-src</code> additionally needs <code>'unsafe-inline'</code> (or a matching hash) — the chat bubble's
          styles are injected inline into a shadow root.
        </p>
      </details>
    </div>
  );
}

// ---- Test ------------------------------------------------------------

function TestTab({ bot }: { bot: Bot }) {
  const account = useAccount();
  // The real widget, running this real bot, in an iframe pointed at a
  // dedicated page (server/src/http/routes/customer.ts's /app/test/:botId).
  // It has to be an iframe rather than an inline embed because the site
  // shell already loads the demo widget, and the loader deliberately
  // initializes only once per page (`window.ChatWidget` guard).
  return (
    <div class="db-tab-body">
      <p class="db-sub">
        This is the real widget answering from this bot's own content — the same thing your visitors get, before you put it on your
        site.
      </p>
      {account && <TestBlockedNotice account={account} />}
      <iframe class="db-test-frame" src={`/app/test/${bot.id}`} title={`Test ${bot.name}`} />
    </div>
  );
}

/**
 * Explains, right above the test widget, exactly why it's about to reply
 * with the vague "reached its usage limit... check your dashboard"
 * fallback — see server/src/http/routes/chat.ts's plan-limit check. That
 * fallback stays deliberately vague (never "trial ended" or "payment
 * failed") because a real visitor on the customer's own site shouldn't
 * read anything that specific; it's safe to be specific here because
 * /app/test/:botId (customer.ts) requires being signed in as this bot's
 * own owner — nobody else ever sees this notice.
 */
function TestBlockedNotice({ account }: { account: AccountInfo }) {
  if (account.level === 'unlimited') {
    // The admin: blocked only by one of their own message limits.
    const cap = reachedCap(account);
    if (!cap) return null;
    const words = capReachedWords(cap);
    return (
      <div class="db-test-blocked" role="status">
        <div class="db-plan-text">
          <strong>The bot below won't answer right now — visitors are offered the contact form instead.</strong>
          <span>
            {words.title} {words.detail}
          </span>
        </div>
        <a class="site-btn site-btn-primary" href="/app/usage">
          Change limits
        </a>
      </div>
    );
  }
  const onHold = isPaidLevel(account.level) && account.billingStatus === 'on_hold';
  const outOfMessages = account.messagesUsed >= account.limits.messages;
  if (!onHold && account.level !== 'expired' && !outOfMessages) return null; // healthy — nothing to explain
  // A trial that ran out (by date or by messages) — offered Basic directly, as on the banner.
  const trialWall =
    (account.level === 'expired' && account.expiredReason === 'trial_ended') || (account.level === 'trial' && outOfMessages);

  let reason: ComponentChildren;
  if (onHold) {
    reason = "Your last payment didn't go through, and we're retrying it. Update your payment method from the Dodo Payments email to turn it back on.";
  } else if (account.level === 'expired') {
    reason =
      account.expiredReason === 'plan_ended'
        ? 'Your plan has ended. Upgrade to turn it back on.'
        : 'Your free trial has ended. Everything you set up is saved — continue on a plan to turn it back on.';
  } else {
    reason =
      account.limits.messagesPer === 'month'
        ? `You've used all ${fmtNum(account.limits.messages)} messages included this month. It resets on the 1st, or upgrade for more.`
        : `You've used all ${fmtNum(account.limits.messages)} trial messages. Upgrade to keep it answering.`;
  }

  return (
    <div class="db-test-blocked" role="status">
      <div class="db-plan-text">
        <strong>The bot below won't answer right now — visitors just see "unable to answer," not why.</strong>
        <span>{reason}</span>
      </div>
      {trialWall ? <ContinueOnBasic /> : <UpgradeButton />}
    </div>
  );
}

// ============================================================================
// Sales demos (admin only — server/src/demos/)
// ============================================================================

/** A demo's progress in the admin's words. Nobody picks pages for a demo,
 * so "discovered" is only the moment between finding them and adding them. */
const DEMO_STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  discovering: 'Reading their site…',
  discovered: 'Teaching the bot…',
  indexing: 'Teaching the bot…',
  processing: 'Teaching the bot…',
  ready: 'Ready',
  failed: 'Failed',
};
const DEMO_IN_FLIGHT = new Set([...IN_FLIGHT, 'discovered']);

/** How long an ended demo is kept — server/src/demos/index.ts's DEMO_GRACE_MS. */
const DEMO_GRACE_MS = 7 * DAY_MS;

/** From this many days left, a demo is flagged as ending soon. */
const DEMO_SOON_DAYS = 7;

function formatDay(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Whole days until `ms`, counted up the way the trial banner counts
 * them: three hours left is "1 day left", never "0 days". */
function daysUntil(ms: number, now: number): number {
  return Math.max(0, Math.ceil((ms - now) / DAY_MS));
}

function days(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** How long a demo has left, in words readable at a glance, and how
 * urgent that is: plenty of time, ending soon, or ended and waiting to be
 * deleted. */
function demoTimeLeft(demo: Demo, now: number): { text: string; detail: string; tone: 'ok' | 'soon' | 'ended' } {
  if (demo.expiresAt > now) {
    const left = daysUntil(demo.expiresAt, now);
    return { text: `${days(left)} left`, detail: `The link works until ${formatDay(demo.expiresAt)}`, tone: left <= DEMO_SOON_DAYS ? 'soon' : 'ok' };
  }
  const untilDeleted = daysUntil(demo.expiresAt + DEMO_GRACE_MS, now);
  return {
    text: untilDeleted > 0 ? `Ended · deleted in ${days(untilDeleted)}` : 'Ended · deleted at the next cleanup',
    detail: `Ended ${formatDay(demo.expiresAt)} · bring it back to keep it`,
    tone: 'ended',
  };
}

function demoHost(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function DemoItem({ demo, busy, onExtend, onDelete }: { demo: Demo; busy: boolean; onExtend: () => void; onDelete: () => void }) {
  // The crawl the demo was built from. Sources added later in the
  // dashboard don't change what the demo is.
  const source = demo.sources[0];
  const status = source?.status ?? 'ready';
  const now = Date.now();
  const ended = demo.expiresAt <= now;
  const ready = status === 'ready' && !ended;
  const time = demoTimeLeft(demo, now);
  const facts = [demoHost(demo.siteUrl), status === 'ready' && source ? `${source.pageCount} page(s)` : null, `built ${formatDay(demo.createdAt)}`];

  return (
    <li class="db-list-item db-demo">
      <div class="db-demo-main">
        <div class="db-demo-title">
          <span class="db-list-title">{demo.prospectName}</span>
          <span class={`db-badge db-badge-${ended ? 'failed' : status}`}>
            {!ended && DEMO_IN_FLIGHT.has(status) && <Spinner small />}
            {ended ? 'Ended' : (DEMO_STATUS_LABELS[status] ?? status)}
          </span>
        </div>
        <p class="db-muted">{facts.filter(Boolean).join(' · ')}</p>
        {demo.recipientName ? (
          <p class="db-demo-recipient">
            Email to: <strong>{demo.recipientName}</strong>
          </p>
        ) : (
          demo.recipientCheckedAt !== null && <p class="db-muted">Email to: no one named on their site</p>
        )}
        <p class="db-demo-time">
          <span class={`db-demo-days db-demo-days-${time.tone}`}>{time.text}</span>
          <span class="db-muted">{time.detail}</span>
        </p>
        {status === 'failed' && source?.error && <p class="db-error">{source.error}</p>}
      </div>
      <div class="db-row-actions">
        {ready && (
          <>
            <a class="site-btn site-btn-primary db-btn-sm" href={`/demo/${demo.slug}`} target="_blank" rel="noopener">
              Open demo page ↗
            </a>
            <CopyButton text={`${window.location.origin}/demo/${demo.slug}`} label="Copy link" />
          </>
        )}
        <a class="site-btn site-btn-ghost db-btn-sm" href={`/app/bots/${demo.botId}`}>
          Customize
        </a>
        <button type="button" class="db-btn-sm" onClick={onExtend} disabled={busy}>
          {ended ? 'Bring back for 30 days' : 'Extend 30 days'}
        </button>
        <button type="button" class="db-btn-danger" onClick={onDelete} disabled={busy}>
          Delete
        </button>
      </div>
    </li>
  );
}

const BATCH_STATUS_LABELS: Record<DemoBatchResult['status'], string> = { created: 'Building', skipped: 'Skipped', failed: 'Failed' };
const SKIP_LABELS: Record<DemoBatchSkip, string> = { repeat: 'Duplicate', exists: 'Duplicate', personal: 'Personal email', invalid: 'Not a website' };
const isDuplicate = (skip?: DemoBatchSkip) => skip === 'repeat' || skip === 'exists';

/** One website, however it was written — the same key the server uses
 * (demoSiteKey in server/src/demos/index.ts): www.acme.com, acme.com/about
 * and https://acme.com/ are all "acme.com"; the port counts. */
function demoSiteKey(raw: string): string | null {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  try {
    const host = new URL(/^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`).host;
    return host ? host.replace(/\.(?=:|$)/, '').replace(/^www\./, '') : null;
  } catch {
    return null;
  }
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** One CSV cell: quoted when it holds a comma, a quote or a line break,
 * and never read as a formula by a spreadsheet (one starting with
 * = + - @ gets a leading '). */
function csvCell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Every demo as CSV rows for a mail merge: the prospect's website, and
 * under "Email" the demo's "Email to" — the person to address, as found on
 * their site; blank, to fill in, when the site names no one clearly. */
export function demosCsv(demos: Array<Pick<Demo, 'siteUrl' | 'recipientName'>>): string {
  const rows = [['Website URL', 'Email'], ...demos.map((d) => [d.siteUrl, d.recipientName])];
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

function downloadDemosCsv(demos: Demo[]) {
  // The byte-order mark tells Excel it's UTF-8, so accented names survive.
  const href = URL.createObjectURL(new Blob(['﻿', demosCsv(demos)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = href;
  link.download = `volgachat-demos-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** Many demos at once: paste prospects' email addresses (or websites),
 * check the list — every duplicate is shown before anything is built —
 * then build one demo per new website and get a link per line to send. */
function DemoBatch({ onBuilt }: { onBuilt: () => Promise<void> }) {
  const [entries, setEntries] = useState('');
  const [checking, setChecking] = useState(false);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The checked list, waiting for "Build". Editing the list drops it. */
  const [plan, setPlan] = useState<DemoBatchPlanEntry[] | null>(null);
  const [results, setResults] = useState<DemoBatchResult[] | null>(null);
  // The list as it is now, for a check that answers after it was edited.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  function edit(value: string) {
    setEntries(value);
    setPlan(null);
  }

  async function check(e: Event) {
    e.preventDefault();
    if (!entries.trim() || checking || building) return;
    setChecking(true);
    setError(null);
    setResults(null);
    try {
      const checked = entries;
      const { entries: planned } = await adminApi.checkDemoBatch(checked);
      // Edited while it was being checked: this plan is for a list that's
      // gone, and "Build" would build the new one unchecked.
      if (entriesRef.current === checked) setPlan(planned);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check that list.');
    } finally {
      setChecking(false);
    }
  }

  async function build() {
    if (building) return;
    setBuilding(true);
    setError(null);
    try {
      const { results } = await adminApi.createDemoBatch(entries);
      setResults(results);
      setPlan(null);
      if (results.some((r) => r.status === 'created')) setEntries('');
      await onBuilt();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build those demos.');
    } finally {
      setBuilding(false);
    }
  }

  const link = (slug: string) => `${window.location.origin}/demo/${slug}`;
  const toBuild = plan?.filter((p) => p.action === 'build').length ?? 0;
  const duplicates = plan?.filter((p) => isDuplicate(p.skip)).length ?? 0;
  const otherSkips = plan ? plan.length - toBuild - duplicates : 0;
  const withLinks = (results ?? []).filter((r) => r.slug);
  const created = (results ?? []).filter((r) => r.status === 'created').length;
  // Tab-separated, so it pastes into two spreadsheet columns for a mail merge.
  const allLinks = withLinks.map((r) => `${r.email ?? r.input}\t${link(r.slug!)}`).join('\n');

  return (
    <details class="db-details db-demo-batch">
      <summary>Build several at once</summary>
      <form class="db-demo-batch-form" onSubmit={check}>
        <textarea
          class="db-input db-textarea"
          aria-label="Prospects' email addresses or websites"
          placeholder={'One per line, e.g.\nana@acme.com\nbo@shop.example\nhttps://example.org'}
          rows={6}
          value={entries}
          onInput={(e) => edit((e.target as HTMLTextAreaElement).value)}
        />
        <p class="db-muted">
          Paste prospects' email addresses or websites, up to 50 at a time. An email address stands for the website at its domain, so
          ana@acme.com builds a demo of acme.com. Only one demo is ever built per website: duplicates are shown when you check the list, before
          anything is built.
        </p>
        {!plan && (
          <div class="db-row-actions">
            <button type="submit" class="site-btn site-btn-primary" disabled={checking || building || !entries.trim()}>
              {checking ? <Busy label="Checking…" /> : 'Check list'}
            </button>
          </div>
        )}
      </form>
      <ErrorNote error={error} />
      {plan && (
        <div class="db-demo-batch-results">
          <div class="db-demo-batch-head">
            <span>
              {plural(toBuild, 'new website')} to build · {plural(duplicates, 'duplicate')}
              {otherSkips > 0 ? ` · ${otherSkips} other line${otherSkips === 1 ? '' : 's'} skipped` : ''}
            </span>
          </div>
          {duplicates > 0 && (
            <p class="db-warning" role="alert">
              {plural(duplicates, 'duplicate')} found. Only one demo is built per website, so {duplicates === 1 ? 'that line is' : 'those lines are'}{' '}
              skipped.
            </p>
          )}
          <ul class="db-list">
            {plan.map((p, i) => (
              <li key={`${i}-${p.input}`} class="db-list-item db-demo-batch-item">
                <div class="db-demo-main">
                  <div class="db-demo-title">
                    <span class="db-list-title">{p.input}</span>
                    <span class={`db-badge ${p.action === 'build' ? 'db-badge-ready' : isDuplicate(p.skip) ? 'db-badge-warn' : 'db-badge-pending'}`}>
                      {p.action === 'build' ? 'New' : SKIP_LABELS[p.skip!]}
                    </span>
                  </div>
                  <p class="db-muted">{[p.email && p.site ? demoHost(p.site) : null, p.reason].filter(Boolean).join(' · ')}</p>
                </div>
                {p.existing && (
                  <div class="db-row-actions">
                    <CopyButton text={link(p.existing.slug)} label="Copy its link" />
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div class="db-row-actions">
            <button type="button" class="site-btn site-btn-primary" onClick={build} disabled={building || toBuild === 0}>
              {building ? <Busy label="Building demos…" /> : toBuild === 0 ? 'Nothing new to build' : `Build ${plural(toBuild, 'demo')}`}
            </button>
            <button type="button" class="site-btn site-btn-ghost" onClick={() => setPlan(null)} disabled={building}>
              Edit list
            </button>
          </div>
        </div>
      )}
      {results && (
        <div class="db-demo-batch-results" role="status">
          <div class="db-demo-batch-head">
            <span>
              {created} demo{created === 1 ? '' : 's'} building · {results.length - created} not built
            </span>
            {withLinks.length > 0 && <CopyButton text={allLinks} label="Copy all links" />}
          </div>
          <ul class="db-list">
            {results.map((r, i) => (
              <li key={`${i}-${r.input}`} class="db-list-item db-demo-batch-item">
                <div class="db-demo-main">
                  <div class="db-demo-title">
                    <span class="db-list-title">{r.input}</span>
                    <span class={`db-badge db-badge-${r.status === 'created' ? 'indexing' : r.status === 'failed' ? 'failed' : isDuplicate(r.skip) ? 'warn' : 'pending'}`}>
                      {r.status === 'skipped' && r.skip ? SKIP_LABELS[r.skip] : BATCH_STATUS_LABELS[r.status]}
                    </span>
                  </div>
                  <p class="db-muted">{[r.email && r.site ? demoHost(r.site) : null, r.reason].filter(Boolean).join(' · ')}</p>
                </div>
                {r.slug && (
                  <div class="db-row-actions">
                    <CopyButton text={link(r.slug)} label="Copy link" />
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p class="db-muted">Each link works once its demo shows Ready in the list below, usually within a few minutes.</p>
        </div>
      )}
    </details>
  );
}

/** Build a chatbot from a prospect's public website and get a link to send
 * them. Admin-only: the server refuses anyone else (adminApi.ts). */
export function AdminDemosPanel() {
  const [demos, setDemos] = useState<Demo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [building, setBuilding] = useState(false);
  /** The demo with an extend or delete in flight. */
  const [working, setWorking] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { demos } = await adminApi.listDemos();
      setDemos(demos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your demos.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the list current while a demo is still being built, so "Ready"
  // shows up without a refresh.
  const anyInFlight = demos?.some((d) => !!d.sources[0] && DEMO_IN_FLIGHT.has(d.sources[0].status)) ?? false;
  useEffect(() => {
    if (!anyInFlight) return;
    const timer = setInterval(() => void load(), 3000);
    return () => clearInterval(timer);
  }, [anyInFlight, load]);

  async function build(e: Event) {
    e.preventDefault();
    if (!url.trim() || building) return;
    setBuilding(true);
    setError(null);
    try {
      await adminApi.createDemo(url.trim(), name.trim() || undefined);
      setUrl('');
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build that demo.');
    } finally {
      setBuilding(false);
    }
  }

  async function extend(demo: Demo) {
    setWorking(demo.botId);
    setError(null);
    try {
      await adminApi.extendDemo(demo.botId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not extend that demo.');
    } finally {
      setWorking(null);
    }
  }

  async function remove(demo: Demo) {
    if (!confirm(`Delete the demo for "${demo.prospectName}"? Its link stops working, and everything read from ${demoHost(demo.siteUrl)} is deleted.`)) return;
    setWorking(demo.botId);
    setError(null);
    try {
      await dashboardApi.deleteBot(demo.botId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that demo.');
    } finally {
      setWorking(null);
    }
  }

  // One demo per website: the one already covering what's typed, if any.
  // The server refuses a second one regardless (409); this says so before
  // the admin even presses the button.
  const typedKey = demoSiteKey(url);
  const typedDuplicate = typedKey ? demos?.find((d) => demoSiteKey(d.siteUrl) === typedKey) : undefined;

  return (
    <div class="db-demos">
      <ErrorNote error={error} />
      <form class="db-inline-form" onSubmit={build}>
        <input
          class="db-input db-demo-url"
          inputMode="url"
          aria-label="Prospect's website"
          placeholder="Prospect's website, e.g. https://example.com"
          value={url}
          onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
        />
        <input
          class="db-input db-demo-name"
          aria-label="Prospect's name"
          placeholder="Their name (optional)"
          maxLength={60}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
        <button type="submit" class="site-btn site-btn-primary" disabled={building || !url.trim() || !!typedDuplicate}>
          {building ? <Busy label="Building…" /> : 'Build demo'}
        </button>
      </form>
      {typedDuplicate && (
        <p class="db-warning" role="alert">
          Duplicate: there is already a demo of {typedKey}, "{typedDuplicate.prospectName}".{' '}
          {typedDuplicate.expiresAt <= Date.now() ? 'It has ended; bring it back from the list below' : 'Use its link from the list below'} instead
          of building another.
        </p>
      )}
      <p class="db-muted">
        Reads up to 50 pages of their public site, skipping anything its robots.txt rules out, and is usually ready in a minute or two. The link
        works for 30 days.
      </p>
      <DemoBatch onBuilt={load} />

      {demos === null ? (
        error ? null : <Loading />
      ) : demos.length === 0 ? (
        <div class="site-empty-state">
          <p>No demos yet.</p>
          <p class="db-muted">Paste a prospect's website above to build one.</p>
        </div>
      ) : (
        <>
          <div class="db-demo-list-head">
            <span class="db-muted">{plural(demos.length, 'demo')}</span>
            <button type="button" class="db-btn-sm" onClick={() => downloadDemosCsv(demos)}>
              Download CSV
            </button>
          </div>
          <ul class="db-list">
            {demos.map((demo) => (
              <DemoItem key={demo.botId} demo={demo} busy={working === demo.botId} onExtend={() => extend(demo)} onDelete={() => remove(demo)} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Admin overview (read-only, all accounts)
// ============================================================================

/** An entry the hidden-owners list takes, as the server stores it
 * (server/src/admin/settings.ts): an email address, or @domain. */
function hiddenOwnerEntry(raw: string): string | null {
  const entry = raw.trim().toLowerCase();
  return /^[^@\s]*@[^@.\s]+(\.[^@.\s]+)+$/.test(entry) && entry.length <= 254 ? entry : null;
}

/** Whether a bot's owner is on the hidden list: their address, or its
 * @domain. A built-in bot has no owner and is never hidden. */
export function isHiddenOwner(email: string | null, hidden: string[]): boolean {
  if (!email) return false;
  const owner = email.toLowerCase();
  return hidden.some((entry) => (entry.startsWith('@') ? owner.endsWith(entry) : owner === entry));
}

/** Whose bots the list leaves out, edited in place and saved to the
 * admin's settings on every change, so it's the same after a reload or in
 * another browser. */
function HiddenOwnersFilter({ hidden, hiddenCount, onChange }: { hidden: string[]; hiddenCount: number; onChange: (next: string[]) => Promise<void> }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(next: string[]) {
    setSaving(true);
    setError(null);
    try {
      await onChange(next);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the list.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function add(e: Event) {
    e.preventDefault();
    const entry = hiddenOwnerEntry(draft);
    if (!entry) {
      setError('Enter an email address, or @domain to hide everyone at a domain.');
      return;
    }
    if (hidden.includes(entry)) {
      setDraft('');
      return;
    }
    if (await save([...hidden, entry])) setDraft('');
  }

  return (
    <div class="db-owner-filter">
      <p class="db-owner-filter-head">
        <strong>Hidden owners</strong>{' '}
        <span class="db-muted">
          — their bots are left out of this list and its totals{hiddenCount > 0 ? ` (${hiddenCount} hidden now)` : ''}.
        </span>
      </p>
      {hidden.length > 0 && (
        <ul class="db-chips" aria-label="Hidden owners">
          {hidden.map((entry) => (
            <li key={entry} class="db-chip">
              <span>{entry}</span>
              <button
                type="button"
                class="db-chip-remove"
                aria-label={`Show bots of ${entry} again`}
                disabled={saving}
                onClick={() => void save(hidden.filter((h) => h !== entry))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form class="db-inline-form" onSubmit={add}>
        <input
          class="db-input db-owner-filter-input"
          type="text"
          inputMode="email"
          aria-label="Email to hide"
          placeholder="Email, or @domain for a whole domain"
          value={draft}
          onInput={(e) => {
            setDraft((e.target as HTMLInputElement).value);
            setError(null);
          }}
        />
        <button type="submit" class="db-btn-sm" disabled={saving || !draft.trim()}>
          Hide
        </button>
      </form>
      <ErrorNote error={error} />
    </div>
  );
}

export function AdminOverviewPage() {
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchAdminOverview>> | null>(null);
  const [hidden, setHidden] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchAdminOverview(), adminApi.getSettings()])
      .then(([overview, { settings }]) => {
        setData(overview);
        setHidden(settings.hiddenOwners);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load the overview.'));
  }, []);

  async function changeHidden(next: string[]) {
    const { settings } = await adminApi.saveSettings({ hiddenOwners: next });
    setHidden(settings.hiddenOwners);
  }

  if (error) return <ErrorNote error={error} />;
  if (!data || !hidden) return <Loading />;

  const bots = data.bots.filter((bot) => !isHiddenOwner(bot.ownerEmail, hidden));
  const sum = (pick: (bot: AdminBotRow) => number) => bots.reduce((total, bot) => total + pick(bot), 0);
  const totals = {
    chunks: sum((b) => b.chunkCount),
    failedSources: sum((b) => b.failedSources),
    tokensLast24h: sum((b) => b.usage.tokensLast24h),
    conversations7d: sum((b) => b.logs.conversations7d),
    messages7d: sum((b) => b.logs.messages7d),
    errors7d: sum((b) => b.logs.errors7d),
  };

  return (
    <div class="db-tab-body">
      <HiddenOwnersFilter hidden={hidden} hiddenCount={data.bots.length - bots.length} onChange={changeHidden} />
      <p class="db-sub">
        {bots.length} bot(s) · {totals.chunks.toLocaleString()} indexed chunks · {totals.failedSources} failed source(s) ·{' '}
        {totals.tokensLast24h.toLocaleString()} tokens in the last 24h
      </p>
      <p class="db-sub">
        Last 7 days: {totals.conversations7d.toLocaleString()} conversation(s) · {totals.messages7d.toLocaleString()} message(s) ·{' '}
        {totals.errors7d.toLocaleString()} error(s). Counts only — conversation content is never shown here.
      </p>
      {!data.usageAvailable && <p class="db-warning">Token usage is unavailable — the rate limiter binding is missing.</p>}

      <div class="db-table-wrap">
        <table class="db-table">
          <thead>
            <tr>
              <th>Bot</th>
              <th>Owner</th>
              <th>Sources</th>
              <th>Chunks</th>
              <th>Failed</th>
              <th>Last crawl</th>
              <th>Tokens (24h)</th>
              <th>Cap used</th>
              <th>Convos (7d)</th>
              <th>Errors (7d)</th>
              <th>Ungrounded (7d)</th>
            </tr>
          </thead>
          <tbody>
            {bots.map((bot: AdminBotRow) => (
              <tr key={bot.id}>
                <td>
                  {bot.name}
                  <br />
                  <code class="db-muted">{bot.id}</code>
                </td>
                <td>{bot.ownerEmail ?? <span class="db-muted">built-in</span>}</td>
                <td>{bot.sourceCount}</td>
                <td>{bot.chunkCount.toLocaleString()}</td>
                <td class={bot.failedSources > 0 ? 'db-cell-bad' : undefined}>{bot.failedSources}</td>
                <td>{formatDate(bot.lastRun)}</td>
                <td class={bot.usage.tokensLast24h >= bot.dailyTokenCap ? 'db-cell-bad' : undefined}>
                  {bot.usage.tokensLast24h.toLocaleString()}
                </td>
                {/* Flagged from 80%, because "who is about to cost me
                    money" needs to be visible before the cap is hit, not
                    after the bot has already stopped answering. */}
                <td class={bot.capUsedFraction >= 0.8 ? 'db-cell-bad' : undefined}>
                  {Math.round(bot.capUsedFraction * 100)}% <span class="db-muted">of {bot.dailyTokenCap.toLocaleString()}</span>
                </td>
                <td>{bot.logs.conversations7d.toLocaleString()}</td>
                <td class={bot.logs.errors7d > 0 ? 'db-cell-bad' : undefined}>{bot.logs.errors7d.toLocaleString()}</td>
                <td class={bot.logs.ungroundedRate7d >= 0.5 ? 'db-cell-bad' : undefined}>
                  {Math.round(bot.logs.ungroundedRate7d * 100)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
